// src/pages/OnboardingFteNominator.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppShell from "../layout/AppShell";
import "./Onboarding.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Country, State, City } from "country-state-city";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setDashboardToast } from "../utils/dashboardToast";
import { ENV, tbGetJson, tbPostJson, tbPostJsonWithBody } from "../api/client";
import { getCpErrorMessages, hasApiValidationFailure as hasCpValidationFailure } from "../utils/cpValidationErrors";
import { validateSecurityIdMmdd8 } from "../utils/securityId";
import { getLookupSecurityExistenceFromPayload, normalizeLookupSecurityExistence } from "../utils/lookupSecurity";
import {
    blockingLoadsForContinue,
    dataLoadToastId,
    loadFailMessage,
    loadOngoingMessage,
    loadTimeoutMessage
} from "../utils/onboardingDataLoadGate";
import { useDataLoadTimeoutToasts } from "../hooks/useDataLoadTimeoutToasts";
import { useDataLoadErrorToasts } from "../hooks/useDataLoadErrorToasts";

const truthy = (v) =>
    v === true || v === 1 || v === "1" || v === "true" || v === "t" || v === "T";

const boolFromEnv = (v) =>
    v === true || v === "true" || v === "1" || v === 1 || v === "Y" || v === "y";

const IS_VALIDATE_CP_NEEDED =
    (ENV.IS_VALIDATE_CP_NEEDED === undefined || ENV.IS_VALIDATE_CP_NEEDED === null || ENV.IS_VALIDATE_CP_NEEDED === "")
        ? true
        : boolFromEnv(ENV.IS_VALIDATE_CP_NEEDED);


const FIRST_LAST_NAME_REGEX = /^(?=[a-zA-Z])[a-zA-Z.\-' ]{2,50}$/;


/* BASES */
const TB_BASE = (ENV.TB_BASE || "").replace(/\/+$/, "");

/* ENDPOINTS (FIXED) */
const ONBOARDING_DRAFT_URL =
    ENV.ONBOARDING_DRAFT_URL || `${TB_BASE}/api/npw-onboarding-draft`;

const ONBOARDING_NEW_URL =
    ENV.ONBOARDING_NEW_URL || `${TB_BASE}/api/npw-onboarding-new`;

const ONBOARDING_EDIT_URL =
    ENV.ONBOARDING_EDIT_URL || `${TB_BASE}/api/npw-onboarding-edit`;
const CP_VALIDATE_URL =
    ENV.CP_VALIDATE_URL ||
    ENV.CINGLEPOINT_URL ||
    ENV.NPW_CP_VALIDATE_URL ||
    ENV.ONBOARDING_CP_VALIDATE_URL ||
    (TB_BASE && `${TB_BASE}/api/v1/cinglepoint`) ||
    "";

const APPROVAL_DETAILS_URL =
    ENV.APPROVAL_DETAILS_URL || `${TB_BASE}/api/npw-approval-details`;

const HINGE_PERSON_LOOKUP = `${(
    (ENV.LOOKUP_URL || ENV.HINGE_BASE || "").replace(/\/+$/, "")
)}`;
const HINGE_ROLE_LIST_URL =
    ENV.HINGE_ROLE_LIST_URL ||
    "https://api-hingedev-directv.com/api/v1/roleCatalog";

/* TB-Hinge proxy endpoints (prefer TB, fallback to direct Hinge) */
const TB_HINGE_PERSON_LOOKUP =
    `${((ENV.TB_HINGE_PERSON_LOOKUP_URL || ENV.TB_HINGE_LOOKUP_URL || ENV.LOOKUP_URL || ENV.HINGE_BASE || "").replace(/\/+$/, ""))}`;

const TB_HINGE_ROLE_LIST_URL =
    ENV.TB_HINGE_ROLE_LIST_URL || ENV.TB_ROLE_CATALOG_URL || HINGE_ROLE_LIST_URL;

/* ✅ NEW: TeamBridge “Hinge Search” API (proxy) URL from client.js ENV (same as Search.jsx) */
const TB_HINGE_SEARCH_URL =
    (ENV.TB_HINGE_SEARCH_URL || ENV.TB_HINGE_PERSON_SEARCH_URL || ENV.TB_HINGE_SEARCH_PROXY_URL || "").replace(/\/+$/, "");

/* Webphone Manager validation - same Hinge validation proxy pattern as OnboardingNominator */
const HINGE_WEBPHONE_VALIDATE_URL = (
    ENV.HINGE_WEBPHONE_VALIDATE_URL ||
    "https://api-hingedev-directv.com/api/v1/validation"
).replace(/\/+$/, "");
const TB_HINGE_WEBPHONE_VALIDATE_URL = (
    ENV.TB_HINGE_WEBPHONE_VALIDATE_URL ||
    ENV.TB_HINGE_VALIDATE_URL ||
    HINGE_WEBPHONE_VALIDATE_URL ||
    ""
).replace(/\/+$/, "");
const WEBPHONE_MANAGER_CACHE_KEY = "tb_webphone_manager_validation_v1";
const normalizeManagerCacheKey = (value) => String(value || "").trim().toLowerCase();
const getManagerValidationPayload = (resp) => {
    if (!resp || typeof resp !== "object") return resp;
    return (resp.response && typeof resp.response === "object")
        ? resp.response
        : (resp.data && typeof resp.data === "object")
            ? resp.data
            : resp;
};
const getManagerAttuidFromResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    return String(r?.attuid ?? r?.attUid ?? r?.attUID ?? "").trim();
};
const getManagerAttuidStatusRawFromResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    return (
        r?.attuidStatus ??
        r?.attuidstatus ??
        r?.attuid_status ??
        r?.attUidStatus ??
        r?.attUIDStatus ??
        r?.personStatus ??
        r?.person_status ??
        r?.status ??
        ""
    );
};
const getManagerAttuidStatusFromResp = (resp) =>
    String(getManagerAttuidStatusRawFromResp(resp)).trim().toLowerCase();
const isActiveManagerAttuidResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    if (!r) return false;

    // Treat only explicit invalid flags as invalid. Some proxy responses return
    // isValid as true/"true" while attuidstatus is the actual ATTUID status flag.
    const rawIsValid = r?.isValid ?? r?.isvalid ?? r?.valid;
    const isValidNorm = String(rawIsValid ?? "").trim().toLowerCase();
    if (rawIsValid === false || isValidNorm === "false" || isValidNorm === "0" || isValidNorm === "no") {
        return false;
    }

    const attuid = getManagerAttuidFromResp(r);
    if (!attuid || attuid.toLowerCase() === "false" || attuid.toLowerCase() === "null") return false;

    const rawStatus = getManagerAttuidStatusRawFromResp(r);
    if (rawStatus === true || rawStatus === 1) return true;

    const status = String(rawStatus || "").trim().toLowerCase();
    return ["active", "true", "t", "yes", "y", "1"].includes(status);
};
const getManagerEmailFromResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    return String(r?.emailId ?? r?.email ?? "").trim();
};
const getLookupSupervisorDtvAnchorId = (lookup) => String(
    lookup?.supervisorDtvAnchorId ??
    lookup?.supervisor_dtv_anchor_id ??
    lookup?.supervisorDtvId ??
    lookup?.supervisor_dtv_id ??
    lookup?.supervisorId ??
    lookup?.supervisor_id ??
    ""
).trim();
const readWebphoneManagerCache = (managerId) => {
    const key = normalizeManagerCacheKey(managerId);
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(WEBPHONE_MANAGER_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed?.[key] || null;
    } catch {
        return null;
    }
};
const writeWebphoneManagerCache = (managerId, response) => {
    const key = normalizeManagerCacheKey(managerId);
    if (!key) return;
    try {
        const raw = sessionStorage.getItem(WEBPHONE_MANAGER_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const next = parsed && typeof parsed === "object" ? parsed : {};
        next[key] = { response };
        sessionStorage.setItem(WEBPHONE_MANAGER_CACHE_KEY, JSON.stringify(next));
    } catch {
        // non-blocking cache failure
    }
};

/* For SSN/securityId existence check (min digits before we call Hinge Search) */
const HINGE_SSN_MIN_DIGITS = Number(ENV.HINGE_SSN_MIN_DIGITS || 8);

/* CP — Country of Citizenship dropdown */
const CP_COUNTRYLIST_URL = (
    ENV.CP_COUNTRYLIST_URL ||
    ENV.CP_COUNTRYLIST_API_URL ||
    "https://gbc0othivl.execute-api.us-west-2.amazonaws.com/test/api/cp-countrylist");


/* STATIC DROPDOWNS */
const STATIC_DD = {
    occupancyCode: ["virtual", "office"],
    workerTypeTax: ["W-2", "1099", "International"],
    nonBillable: ["Yes", "No"],
};

const EyeIcon = ({ size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

/* WORKER TYPE (Page 2 banner + API payload) */
const DEFAULT_WORKER_TYPE = "Full Time";

/* TZ */
const getTimeZones = () => {
    const tz = Intl.supportedValuesOf("timeZone");
    const now = new Date();
    return tz.map((z) => {
        const off = new Intl.DateTimeFormat("en-US", {
            timeZone: z,
            timeZoneName: "shortOffset",
            hour: "2-digit",
            minute: "2-digit",
        })
            .formatToParts(now)
            .find((p) => p.type === "timeZoneName")?.value;
        return { label: `(UTC${off}) ${z}`, value: z };
    });
};
const timeZoneOptions = getTimeZones();

/* Wider dropdown styles */
const wideSelectStyles = {
    container: (base) => ({
        ...base,
        minWidth: 200,
        width: "100%",
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

/* HELPERS */
const serializeDate = (d) => {
    if (!d) return null;
    const parsed = d instanceof Date ? d : parseAnyDate(d);
    if (!(parsed instanceof Date) || Number.isNaN(+parsed)) return null;
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(
        parsed.getDate()
    ).padStart(2, "0")}`;
};

const fmtDate = (d) =>
    d
        ? new Date(d).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        })
        : "";

// Robust date parser for API strings like "01/01/2025" or ISO
function parseAnyDate(s) {
    if (!s) return null;
    const d1 = new Date(s);
    if (!isNaN(d1)) return d1;
    const m = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(String(s));
    if (m) {
        const mm = parseInt(m[1], 10) - 1;
        const dd = parseInt(m[2], 10);
        const yy = parseInt(m[3], 10);
        const d = new Date(yy, mm, dd);
        if (!isNaN(d)) return d;
    }
    return null;
}

const collectApiMessages = (value, out = [], depth = 0) => {
    if (depth > 4 || value == null) return out;
    if (typeof value === "string") {
        const msg = value.trim();
        if (msg) out.push(msg);
        return out;
    }
    if (Array.isArray(value)) {
        value.forEach((item) => collectApiMessages(item, out, depth + 1));
        return out;
    }
    if (typeof value === "object") {
        const keys = [
            "message",
            "error",
            "errorMessage",
            "detail",
            "reason",
            "errors",
            "validationErrors",
            "fieldErrors",
            "results",
            "body",
            "agentBulkResults",
            "messages",
        ];
        keys.forEach((k) => collectApiMessages(value?.[k], out, depth + 1));
    }
    return out;
};

const hasApiValidationFailure = (resp) => {
    return hasCpValidationFailure(resp);
};

/* =========================
   SECURITY ID MASKING RULES
   ========================= */

const isUnitedStatesSelected = (countryLabel = "") => {
    const c = String(countryLabel || "").trim().toLowerCase();
    return (
        c === "united states" ||
        c === "united states of america" ||
        c === "usa" ||
        c === "us"
    );
};

// For US: ___-__-____ (9 digits)
const US_TEMPLATE = "   -  -    ";
const US_DIGIT_POS = [0, 1, 2, 4, 5, 7, 8, 9, 10];

// For Non-US: f-__-__-____ (8 digits)
const NONUS_TEMPLATE = "f-  -  -    ";
const NONUS_DIGIT_POS = [2, 3, 5, 6, 8, 9, 10, 11];

function templateFor(isUS) {
    return isUS ? US_TEMPLATE : NONUS_TEMPLATE;
}
function digitPosFor(isUS) {
    return isUS ? US_DIGIT_POS : NONUS_DIGIT_POS;
}
function maxDigitsFor(isUS) {
    return isUS ? 9 : 8;
}

function extractDigits(s, isUS) {
    const str = String(s || "");
    if (isUS) {
        // Only digits allowed for US
        return str.replace(/\D/g, "");
    }
    if (isUS === false) {
        // For non-US: first 4 must be digits, last 4 can be alphanumeric
        let out = "";
        for (let i = 0; i < str.length && out.length < 8; i++) {
            const ch = str[i];
            if (out.length < 4) {
                if (/\d/.test(ch)) {
                    out += ch;
                }
            } else {
                if (/\w/.test(ch)) {
                    out += ch;
                }
            }
        }
        return out?.toUpperCase();
    } else {
        return str.replace(/\D/g, "");
    }
}

// Prefer plaintext/decrypted Security ID fields when present.
// Many APIs return masked values in `security_id` (e.g., "***-**-____"), which contains no digits.
// Eye-toggle requires digits to be available in state to render unmasked format.
function resolveSecurityDigits(api) {
    if (!api) return "";
    const candidates = [
        api.security_id_plaintext,
        api.securityIdPlaintext,
        api.security_id_unmasked,
        api.securityIdUnmasked,
        api.security_id_value,
        api.securityIdValue,
        api.security_id_raw,
        api.securityIdRaw,
        api.security_id, // may be masked
        api.securityId,
        // Hinge DTV lookup can return only last-4 digits
        api.ssnLast4d,
        api.ssn_last4d,
        api.ssnLast4,
        api.ssn_last4,
        api.security_id_hash, // not usable for display but may contain digits in some legacy payloads
    ];
    for (const v of candidates) {
        const d = extractDigits(v ?? "");
        if (d) return d;
    }
    return "";
}


/* =========================
   HINGE SEARCH (SSN existence check)
   - Uses the same TB_HINGE_SEARCH_URL as Search.jsx
   - Parses multiple possible response shapes from the proxy
   ========================= */
function normalizeHingePeople(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload === "string") {
        try { return normalizeHingePeople(JSON.parse(payload)); } catch { return []; }
    }
    const wrapped = payload?.response && typeof payload.response === "object" ? payload.response : null;
    const p = wrapped || payload;
    return (
        p.personDetails ||
        p.personList ||
        p.personDetailsList ||
        p.data ||
        p.results ||
        p.items ||
        []
    );
}

function hingePersonFullName(p) {
    const first = String(p?.firstName ?? p?.first_name ?? "").trim();
    const last = String(p?.lastName ?? p?.last_name ?? "").trim();
    const full = `${first} ${last}`.trim();
    return full || "";
}

function clampDigitsToFormat(isUS, digits) {
    const max = maxDigitsFor(isUS);
    return String(digits || "").slice(0, max);
}

// Convert digits entered => masked display that keeps separators (and 'f' for non-US)
function buildMaskedDisplay(isUS, digits) {
    const tpl = templateFor(isUS).split("");
    const pos = digitPosFor(isUS);
    const n = Math.min(digits.length, pos.length);
    for (let i = 0; i < pos.length; i++) {
        tpl[pos[i]] = i < n ? "*" : "_";
    }
    return tpl.join("");
}

// Show only last4 in review, rest masked
function buildMaskedLast4(isUS, digits) {
    const tpl = templateFor(isUS).split("");
    const pos = digitPosFor(isUS);
    const last4 = String(digits || "").slice(-4);
    const total = maxDigitsFor(isUS);

    for (let i = 0; i < pos.length; i++) tpl[pos[i]] = "*";

    const start = Math.max(0, total - 4);
    for (let i = start; i < total; i++) {
        const idxInLast4 = i - start;
        tpl[pos[i]] = last4[idxInLast4] || "*";
    }
    return tpl.join("");
}

// Build "unmasked" formatted value for eye toggle
function buildUnmaskedFormatted(isUS, digits) {
    const d = clampDigitsToFormat(isUS, extractDigits(digits, isUS));
    if (!d) return "";
    if (isUS) {
        const a = d.slice(0, 3);
        const b = d.slice(3, 5);
        const c = d.slice(5, 9);
        return `${a}${d.length > 3 ? "-" : ""}${b}${d.length > 5 ? "-" : ""}${c}`.replace(
            /-$/,
            ""
        );
    }
    const a = d.slice(0, 2);
    const b = d.slice(2, 4);
    const c = d.slice(4, 8);
    return `f-${a}${d.length > 2 ? "-" : ""}${b}${d.length > 4 ? "-" : ""}${c}`.replace(
        /-$/,
        ""
    );
}

function setCaretToNextSlot(inputEl, isUS, digitsLen) {
    if (!inputEl) return;
    const pos = digitPosFor(isUS);
    const nextIndex =
        pos[Math.min(digitsLen, pos.length - 1)] ??
        (isUS ? US_TEMPLATE.length : NONUS_TEMPLATE.length);
    const caret =
        digitsLen >= pos.length
            ? isUS
                ? US_TEMPLATE.length
                : NONUS_TEMPLATE.length
            : nextIndex;
    try {
        inputEl.setSelectionRange(caret, caret);
    } catch { }
}

function setCaretToSlotIndex(inputEl, idx) {
    if (!inputEl) return;
    try {
        inputEl.setSelectionRange(idx, idx);
    } catch { }
}

/* API wrappers */
const apiSaveDraft = (payload) => tbPostJson(ONBOARDING_DRAFT_URL, payload);
const apiSubmitNew = (payload) => tbPostJsonWithBody(ONBOARDING_NEW_URL, payload);
const apiSubmitEdit = (payload) =>
    tbPostJsonWithBody(ONBOARDING_EDIT_URL, payload, { fetchInit: { method: "POST" } }); // PUT/POST
const apiValidateCp = (payload) => {
    if (!CP_VALIDATE_URL) throw new Error("CP_VALIDATE_URL not configured");
    return tbPostJsonWithBody(CP_VALIDATE_URL, payload, { fetchInit: { method: "POST" } });
};

const apiFetchById = (id) => {
    /*TODO:pentest*/
    // Build v query
    const dtvidRaw = sessionStorage.getItem("tb_dtvid") || "";
    const roleRaw = sessionStorage.getItem("tb_role") || sessionStorage.getItem("tb_role_raw") || "";
    const ts = Date.now();
    const vRaw = `${dtvidRaw}_*v*_${ts}`;
    const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);
    const url = `${APPROVAL_DETAILS_URL}?npw_id=${encodeURIComponent(id)}&role=${encodeURIComponent(roleRaw)}&v=${encodeURIComponent(vParam)}&_=${Date.now()}`;
    
    /*TODO:pentest*/
    return tbGetJson(url);
    // return tbGetJson(`${APPROVAL_DETAILS_URL}?npw_id=${encodeURIComponent(id)}`);
}

/* COMPONENT */
export default function OnboardingFteNominator({ npwId = null, backToMain = null }) {
    const { id: routeId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Robust "Back" behavior for edit routes (consistent with other onboarding pages)
    const handleBackToCaller = () => {
        try {
            if (typeof backToMain === "function") {
                backToMain();
                return;
            }
        } catch { /* ignore */ }

        const from = typeof location?.state?.from === "string" ? location.state.from : "";
        if (from) {
            navigate(from, { replace: true });
            return;
        }

        try {
            const host = (sessionStorage.getItem("npw.search.host") || "").trim();
            const ret = (sessionStorage.getItem("npw.search.returnPath") || "").trim();
            const target = host || ret;
            if (target) {
                navigate(target, { replace: true });
                return;
            }
        } catch { /* ignore */ }

        try {
            if (window.history.length > 1) {
                navigate(-1);
                return;
            }
        } catch { /* ignore */ }

        navigate("/dashboard", { replace: true });
    };

    const hasExternalCallerTarget = (() => {
        const from = typeof location?.state?.from === "string" ? location.state.from.trim() : "";
        let host = "";
        let ret = "";
        try {
            host = (sessionStorage.getItem("npw.search.host") || "").trim();
            ret = (sessionStorage.getItem("npw.search.returnPath") || "").trim();
        } catch { /* ignore */ }
        const target = (from || host || ret || "").trim();
        return Boolean(target && (/^\/(approvals|manage-requests)(\b|\/|\?)/.test(target)));
    })();

    /**
     * Toast UX rule:
     * - When we redirect immediately after a successful action, the current route can unmount before the toast
     *   is visible. We therefore show an immediate toast and, when redirecting to the dashboard, persist it via
     *   setDashboardToast.
     */
    const successThenDashboard = (message) => {
        try { toast.success(message); } catch { }
        try { setDashboardToast(message, "success"); } catch { }
        setTimeout(() => navigate("/dashboard", { replace: true }), 250);
    };

    const successThenNavigate = (message, path, options) => {
        try { toast.success(message); } catch { }
        setTimeout(() => navigate(path, options || {}), 250);
    };

    const routeToken = (() => {
        const v = (routeId || "").toString().trim();
        if (!v) return "";
        try {
            return decodeURIComponent(v);
        } catch {
            return v;
        }
    })();

    const routeDtvid = (() => {
        const t = (routeToken || "").trim();
        const m = /^(?:DTVID|dtvid)\s*=\s*(.+)$/i.exec(t);
        return m ? String(m[1] || "").trim() : "";
    })();

    const routeNpwId = (() => {
        const v = (routeToken || "").trim();
        if (!v) return null;

        // Accept explicit key-value route tokens (compatibility with other onboarding pages)
        // Examples: "npw_id=123", "npwid=123", "npwId=123", "id=123", "number=123"
        const kv = /^(?:npw_id|npwid|npwId|id|number)\s*=\s*(.+)$/i.exec(v);
        if (kv) {
            const raw = String(kv[1] || "").trim();
            return raw || null;
        }

        // Support routes like "/new/222" where the route param is "new/222"
        // (some routers capture the full remainder as a single param)
        const parts = String(v)
            .split("/")
            .map((p) => p.trim())
            .filter(Boolean);
        if (parts[0]?.toLowerCase() === "new") return parts[1] || null;
        if (v.toLowerCase() === "new") return null;
        if (/^(?:DTVID|dtvid)\s*=/.test(v)) return null;
        return v;
    })();

    // Also accept query-string ids (dtvid/npwId/npw_id/npwid/number) for parity with EmpSupplierAdmin.
    const qpNpwId = (() => {
        try {
            const sp = new URLSearchParams(location.search);
            return (
                (sp.get("npw_id") || "").trim() ||
                (sp.get("npwId") || "").trim() ||
                (sp.get("npwid") || "").trim() ||
                (sp.get("id") || "").trim() ||
                (sp.get("number") || "").trim() ||
                null
            );
        } catch {
            return null;
        }
    })();

    const effectiveNpwId =
        npwId != null && String(npwId).trim()
            ? String(npwId).trim()
            : (qpNpwId || routeNpwId);

    // Per UX: treat as edit ONLY when route has a real :id (npw_id) or an external npwId prop exists.
    const isEditRoute = !!(effectiveNpwId && String(effectiveNpwId).trim());

    const getSessionIdentity = () => {
        const SS = sessionStorage;
        return {
            submitter_name: (SS.getItem("tb_name") || "").trim(),
            submitter_email: (SS.getItem("tb_email") || "").trim(),
            approver: (SS.getItem("approver") || "").trim(),
            loggedInUserRole: (SS.getItem("tb_role") || "").trim(),
            submitter_dtvid: (SS.getItem("tb_dtvid") || "").trim(),
        };
    };

    const ensureIdentityOrForceSSO = () => {
        const { submitter_name, submitter_email } = getSessionIdentity();
        if (submitter_name && submitter_email) return true;

        toast.error("Session expired. Redirecting to sign in...");
        try {
            sessionStorage.removeItem("tb_login_kicked");
            sessionStorage.setItem("tb_login_kicked", "0");
            const href = (window.location?.href || "/").split("#")[0];
            sessionStorage.setItem("tb_return_to", href);
        } catch { }
        window.location.assign("/");
        return false;
    };

    const searchParams = new URLSearchParams(location.search);
    const isReactiveAttuidFlow = (() => {
        const flag = (searchParams.get("reactivate_attuid") || searchParams.get("reactive_attuid") || "").trim();
        const isFlagTruthy = (v) => v === true || v === 1 || v === "1" || v === "true" || v === "t" || v === "T";
        return isFlagTruthy(flag) || isFlagTruthy(location?.state?.reactivateAttuid);
    })();
    const effectiveDtvid = ((searchParams.get("dtvId") || searchParams.get("dtvid") || location?.state?.dtvId || routeDtvid || "")).trim();
    const dtvid = effectiveDtvid;
    const hasRealNpwId = /^\d+$/.test(String(effectiveNpwId || "").trim());
    const showDtvidAttuidPrefill = Boolean((effectiveDtvid && String(effectiveDtvid).trim()) && !hasRealNpwId);

    // Security ID edit/prefill context (NPW id OR DTVID prefill routes)
    const isSecurityEditContext = Boolean(isEditRoute || (effectiveDtvid && String(effectiveDtvid).trim()));
    const dtvidDraftKey = (d) => `tb_draft_npw_id:fetn:${String(d || "").trim().toLowerCase()}`;
    const getPersistedDraftNpwIdForDtvid = (d) => {
        const key = dtvidDraftKey(d);
        if (!key) return null;
        try {
            return localStorage.getItem(key) || sessionStorage.getItem(key) || null;
        } catch {
            try {
                return sessionStorage.getItem(key) || null;
            } catch {
                return null;
            }
        }
    };
    const setPersistedDraftNpwIdForDtvid = (d, id) => {
        const key = dtvidDraftKey(d);
        if (!key) return;
        const val = String(id || "").trim();
        if (!val) return;
        try {
            localStorage.setItem(key, val);
        } catch { }
        try {
            sessionStorage.setItem(key, val);
        } catch { }
    };
    const clearPersistedDraftNpwIdForDtvid = (d) => {
        const key = dtvidDraftKey(d);
        if (!key) return;
        try {
            localStorage.removeItem(key);
        } catch { }
        try {
            sessionStorage.removeItem(key);
        } catch { }
    };

    const [step, setStep] = useState(1);
    const [workerTypeLabel] = useState(DEFAULT_WORKER_TYPE);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [cpValidation, setCpValidation] = useState({ status: "idle", message: "" });
    const [webphoneValidation, setWebphoneValidation] = useState({ status: "idle", message: "" });
    const [editData, setEditData] = useState(null);

    const editHeadingOverride = useMemo(() => {
        if (!isEditRoute) return "";
        const statusKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_-]/g, "");
        const k = statusKey(editData?.personStatus ?? editData?.person_status ?? editData?.status);
        if (k === "inactive") return "Rehire Onboarding";
        if (k === "active") return "Modify Onboarding";
        return "";
    }, [isEditRoute, editData?.status, editData?.personStatus, editData?.person_status]);

    // Security ID: in edit/prefill flows, inputs remain read-only until user clicks "Edit"
    const [securityEditEnabled, setSecurityEditEnabled] = useState(false);
    const [securityInitialDigits, setSecurityInitialDigits] = useState("");
    const securityInitCapturedRef = useRef(false);
    const securityInitialIsUSRef = useRef(null);
    const [lookupSecExistFlag, setLookupSecExistFlag] = useState("");

    const [hingeRoleCatalog, setHingeRoleCatalog] = useState([]);
    const [hingeLookup, setHingeLookup] = useState(null);

    const [dataLoads, setDataLoads] = useState({
        roles: { field: "Role", status: TB_HINGE_ROLE_LIST_URL ? "loading" : "idle", startedAt: TB_HINGE_ROLE_LIST_URL ? Date.now() : null }
    });

    useDataLoadTimeoutToasts(dataLoads, { timeoutMs: 20000 });
    useDataLoadErrorToasts(dataLoads);

    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const hasStatesForSelectedCountry = useMemo(() => {
        const countryCode = selectedCountry?.value;
        if (!countryCode) return false;
        try {
            return (State.getStatesOfCountry(countryCode) || []).length > 0;
        } catch {
            return stateOptions.length > 0;
        }
    }, [selectedCountry?.value, stateOptions.length]);
    const hasCitiesForSelectedState = useMemo(() => {
        const countryCode = selectedCountry?.value;
        const stateCode = selectedState?.value;
        if (!countryCode || !stateCode) return false;
        try {
            return (City.getCitiesOfState(countryCode, stateCode) || []).length > 0;
        } catch {
            return cityOptions.length > 0;
        }
    }, [selectedCountry?.value, selectedState?.value, cityOptions.length]);
    const isStateRequired = Boolean(selectedCountry && hasStatesForSelectedCountry);
    const isCityRequired = Boolean(isStateRequired && selectedState && hasCitiesForSelectedState);
    const [selectedTz, setSelectedTz] = useState(null);
    const [ctzn, setCtz] = useState(null);

    const [citizenshipOptions, setCitizenshipOptions] = useState([]);

    const [fieldErrors, setFieldErrors] = useState({});

    // ✅ NEW: SSN/Security ID existence check via TB_HINGE_SEARCH_URL (debounced)
    const [ssnAssociation, setSsnAssociation] = useState({ status: "idle", message: "" }); // idle|checking|found|none|error
    const ssnDebounceRef = useRef(null);
    const ssnReqSeqRef = useRef(0);

    const [workerTypeTax, setWorkerTypeTax] = useState("");
    const [nonBillable, setNonBillable] = useState("");

    // When we programmatically set Country/State during edit prefill, we must not
    // clear downstream fields (State/City/Security ID). These refs gate that behavior.
    const suppressCountryCascadeRef = useRef(false);
    const suppressStateCascadeRef = useRef(false);

    // Eye toggles
    const [showSecurityId, setShowSecurityId] = useState(false);
    const [showConfirmSecurityId, setShowConfirmSecurityId] = useState(false);

    const [draftNpwId, setDraftNpwId] = useState(() => {
        const v = sessionStorage.getItem("tb_draft_npw_id");
        return v ? String(v) : null;
    });
    const [draftClientRequestId, setDraftClientRequestId] = useState(() => {
        const v = sessionStorage.getItem("tb_draft_client_request_id");
        return v ? String(v) : null;
    });

    const roleCatalogFetchedRef = useRef(false);
    const validatedWebphoneManagerRef = useRef(null);
    const prefillKey = JSON.stringify({ dtvid: dtvid || null, npwId: effectiveNpwId || null });
    const prefillFetchedKeysRef = useRef(new Set());

    useEffect(() => {
        if (!TB_HINGE_PERSON_LOOKUP) return;

        const hasNumericNpwId = /^\d+$/.test(String(effectiveNpwId || "").trim());
        const targetDtvId = String(editData?.dtvId || editData?.dtv_id || dtvid || "").trim();
        if (!hasNumericNpwId || !targetDtvId) return;

        let canceled = false;
        (async () => {
            try {
                const resp = await tbGetJson(`${TB_HINGE_PERSON_LOOKUP}?dtvId=${encodeURIComponent(targetDtvId)}`);
                if (canceled) return;
                setLookupSecExistFlag(getLookupSecurityExistenceFromPayload(resp));
            } catch {
                if (!canceled) setLookupSecExistFlag("");
            }
        })();

        return () => {
            canceled = true;
        };
    }, [effectiveNpwId, editData?.dtvId, editData?.dtv_id, dtvid]);

    const initialVal = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        citizenship: "",
        country: "",
        state: "",
        city: "",
        timeZone: "",
        occupancyCode: "",
        securityId: "", // digits only
        validateSecurity: "", // digits only (UI only)
        startDate: null,
        endDate: null,
        // Keep parity with other onboarding roles: backend expects a `role` field.
        // This page derives it from the selected ATT Role when the user doesn't explicitly provide one.
        role: "",

        workerTypeTax: "",
        nonBillable: "",

        attRoleKey: "",
        attRoleDtvRoleId: "",
        attRoleDtvRoleName: "",
        attRoleCpRoleId: "",
        attRoleCpRoleDesc: "",
        attRoleCpProfileId: "",
        attRoleCpProfileDesc: "",
        webphoneManagerId: "",
        webphone_manager_attuid: "",
        attuid: "",
        attuidRequired: "Yes",
    };

    const [form1, setForm1] = useState(initialVal);
    const requiresCpValidateBeforeSubmit = IS_VALIDATE_CP_NEEDED;

    useEffect(() => {
        setCpValidation((prev) => (prev.status === "idle" ? prev : { status: "idle", message: "" }));
    }, [form1]);

    useEffect(() => {
        if (!TB_HINGE_WEBPHONE_VALIDATE_URL) return;

        // FTE preload rule: use supervisorDtvAnchorId from the lookup response as mgrDtvId
        // for Hinge Webphone Manager validation. The DB column webphone_manager_attuid
        // is set only when the manager validation response has attuidStatus = active.
        const lookupSupervisorManagerId = getLookupSupervisorDtvAnchorId(hingeLookup);
        const managerVal = String(
            lookupSupervisorManagerId ||
            form1.webphoneManagerId ||
            editData?.webphone_manager ||
            editData?.webphoneManager ||
            editData?.webphone_manager_id ||
            editData?.webphoneManagerId ||
            ""
        ).trim();

        const applyManagerValidation = (resp, managerIdAtCall) => {
            const activeAttuid = isActiveManagerAttuidResp(resp);
            const mgrAttuid = activeAttuid ? getManagerAttuidFromResp(resp) : "";
            const mgrEmailId = activeAttuid ? getManagerEmailFromResp(resp) : "";

            setForm1((prev) => {
                const currentManager = String(prev?.webphoneManagerId || "").trim();
                const expectedManager = String(managerIdAtCall || "").trim();
                const lookupManager = getLookupSupervisorDtvAnchorId(hingeLookup);
                if (currentManager && currentManager !== expectedManager && !lookupManager) return prev;
                return {
                    ...prev,
                    webphoneManagerId: lookupManager || currentManager || expectedManager,
                    webphoneManagerEmail: mgrEmailId,
                    webphone_manager_email: mgrEmailId,
                    webphone_manager_attuid: mgrAttuid || null,
                };
            });

            setWebphoneValidation({
                status: activeAttuid ? "ok" : "error",
                message: activeAttuid ? "" : "Webphone Manager does not have an active ATTUID in Hinge",
            });
        };

        if (!managerVal) {
            setWebphoneValidation({ status: "idle", message: "" });
            setForm1((prev) => ({
                ...prev,
                webphoneManagerEmail: "",
                webphone_manager_email: "",
                webphone_manager_attuid: null,
            }));
            return;
        }

        const cached = readWebphoneManagerCache(managerVal);
        if (cached?.response) {
            applyManagerValidation(cached.response, managerVal);
            return;
        }

        let cancelled = false;
        setWebphoneValidation({ status: "checking", message: "" });
        const handle = setTimeout(async () => {
            try {
                const base = TB_HINGE_WEBPHONE_VALIDATE_URL;
                const sep = base.includes("?") ? "&" : "?";
                const url = `${base}${sep}mgrDtvId=${encodeURIComponent(managerVal)}`;
                const resp = await tbGetJson(url);
                writeWebphoneManagerCache(managerVal, resp);
                if (!cancelled) applyManagerValidation(resp, managerVal);
            } catch {
                if (!cancelled) {
                    setForm1((prev) => ({
                        ...prev,
                        webphone_manager_attuid: null,
                    }));
                    setWebphoneValidation({
                        status: "error",
                        message: "Webphone Manager validation failed",
                    });
                }
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [form1.webphoneManagerId, editData, hingeLookup]);

    const getCurrentWebphoneManagerId = () => String(getLookupSupervisorDtvAnchorId(hingeLookup) || form1?.webphoneManagerId || "").trim();

    const getValidatedWebphoneManagerForCurrentManager = () => {
        const managerId = getCurrentWebphoneManagerId();
        const validated = validatedWebphoneManagerRef.current;
        if (!managerId) {
            return { managerId: "", webphoneManagerAttuid: null, webphone_manager_attuid: null, response: null };
        }
        if (validated && String(validated.managerId || "").trim().toLowerCase() === managerId.toLowerCase()) {
            return validated;
        }
        return null;
    };

    const validateWebphoneManagerBeforeApi = async () => {
        // Must run synchronously before CP validate / Draft. Submit reuses the result captured during Validate.
        // FTE rule: take supervisorDtvAnchorId from the lookup response and send it as mgrDtvId.
        const managerId = getCurrentWebphoneManagerId();

        if (!managerId) {
            const emptyValidation = { managerId: "", webphoneManagerAttuid: null, webphone_manager_attuid: null, response: null };
            validatedWebphoneManagerRef.current = emptyValidation;
            setForm1((prev) => ({
                ...prev,
                webphoneManagerId: "",
                webphoneManagerEmail: "",
                webphone_manager_email: "",
                webphone_manager_attuid: null,
            }));
            setWebphoneValidation({ status: "idle", message: "" });
            return emptyValidation;
        }

        const applyAndReturn = (resp) => {
            const isActive = isActiveManagerAttuidResp(resp);
            const attuid = isActive ? getManagerAttuidFromResp(resp) : "";
            const emailId = isActive ? getManagerEmailFromResp(resp) : "";

            setForm1((prev) => ({
                ...prev,
                webphoneManagerId: managerId,
                webphoneManagerEmail: emailId,
                webphone_manager_email: emailId,
                webphone_manager_attuid: attuid || null,
            }));
            setWebphoneValidation({
                status: isActive ? "ok" : "error",
                message: isActive ? "" : "Webphone Manager does not have an active ATTUID in Hinge",
            });

            const validationResult = {
                managerId,
                webphoneManagerAttuid: attuid || null,
                webphone_manager_attuid: attuid || null,
                webphoneManagerEmail: emailId || "",
                webphone_manager_email: emailId || "",
                response: resp,
            };
            validatedWebphoneManagerRef.current = validationResult;
            return validationResult;
        };

        // Do not use cache in the pre-API path. This must visibly call Hinge validation
        // before CP/New/Edit/Draft so the payload uses the latest attuidStatus.
        if (!TB_HINGE_WEBPHONE_VALIDATE_URL) {
            const failedValidation = { managerId, webphoneManagerAttuid: null, webphone_manager_attuid: null, response: null };
            validatedWebphoneManagerRef.current = failedValidation;
            setForm1((prev) => ({ ...prev, webphoneManagerId: managerId, webphone_manager_attuid: null }));
            setWebphoneValidation({ status: "error", message: "Webphone Manager validation URL is not configured" });
            return failedValidation;
        }

        setWebphoneValidation({ status: "checking", message: "Validating Webphone Manager..." });
        try {
            const base = TB_HINGE_WEBPHONE_VALIDATE_URL;
            const sep = base.includes("?") ? "&" : "?";
            const url = `${base}${sep}mgrDtvId=${encodeURIComponent(managerId)}`;
            const resp = await tbGetJson(url);
            writeWebphoneManagerCache(managerId, resp);
            return applyAndReturn(resp);
        } catch (e) {
            const failedValidation = { managerId, webphoneManagerAttuid: null, webphone_manager_attuid: null, response: null };
            validatedWebphoneManagerRef.current = failedValidation;
            setForm1((prev) => ({
                ...prev,
                webphoneManagerId: managerId,
                webphoneManagerEmail: "",
                webphone_manager_email: "",
                webphone_manager_attuid: null,
            }));
            setWebphoneValidation({ status: "error", message: "Webphone Manager validation failed" });
            return failedValidation;
        }
    };

    const securityRef = useRef(null);
    const confirmSecurityRef = useRef(null);
    const securityTouchedRef = useRef(false);
    const confirmSecurityTouchedRef = useRef(false);

    const isUS = useMemo(() => isUnitedStatesSelected(form1.country), [form1.country]);
    const securityInitialHadValue = Boolean(securityInitialDigits);
    const hingeStatusLc = String(hingeLookup?.personStatus || "").trim().toLowerCase();
    const hasLookupAttuid = Boolean(String(
        hingeLookup?.attuid ??
        hingeLookup?.attUid ??
        hingeLookup?.attUID ??
        editData?.attuid ??
        editData?.attUid ??
        editData?.attUID ??
        form1.attuid ??
        ""
    ).trim());
    const hasLookupSecurity = lookupSecExistFlag === "yes";
    const dtvPrefillExisting =
        Boolean(dtvid) &&
        !isEditRoute &&
        /(^|\b)(active|inactive|terminated)(\b|$)/.test(hingeStatusLc);
    const canLockSecurityForFte = hasLookupSecurity && hasLookupAttuid;

    // =========================
    // Security ID edit/lock gating
    // (must be after hingeLookup + form1 initialization)
    // =========================
    useEffect(() => {
        if (!isSecurityEditContext) {
            securityInitCapturedRef.current = false;
            setSecurityEditEnabled(false);
            setSecurityInitialDigits("");
            securityInitialIsUSRef.current = null;
            return;
        }

        const lookupSecExistKnown = lookupSecExistFlag === "yes" || lookupSecExistFlag === "no";
        if (!lookupSecExistKnown) return;

        if (securityInitCapturedRef.current) return;

        const hasWorkerAttuid = Boolean(String(
            hingeLookup?.attuid ??
            hingeLookup?.attUid ??
            hingeLookup?.attUID ??
            editData?.attuid ??
            editData?.attUid ??
            editData?.attUID ??
            form1.attuid ??
            ""
        ).trim());
        if (lookupSecExistFlag !== "yes" || !hasWorkerAttuid) {
            securityInitCapturedRef.current = true;
            setSecurityInitialDigits("");
            securityInitialIsUSRef.current = null;
            return;
        }

        // Prefer full edit payload when available.
        if (editData) {
            securityInitCapturedRef.current = true;
            setSecurityInitialDigits(extractDigits(editData.securityId || "", isUS));
            securityInitialIsUSRef.current = isUnitedStatesSelected(editData.country);
            return;
        }

        // DTV prefill existing (Active/Terminated): capture last-4 when available.
        const statusLc = String(hingeLookup?.personStatus || "").trim().toLowerCase();
        const isExisting = /(^|\b)(active|inactive|terminated)(\b|$)/.test(statusLc);
        if (dtvid && isExisting && canLockSecurityForFte && hingeLookup) {
            const last4 = extractDigits(
                hingeLookup?.ssnLast4d ||
                hingeLookup?.ssn_last4d ||
                hingeLookup?.ssnLast4 ||
                hingeLookup?.ssn_last4 ||
                ""
            );
            securityInitCapturedRef.current = true;
            setSecurityInitialDigits(last4 || "");
            securityInitialIsUSRef.current = isUnitedStatesSelected(
                form1.country || hingeLookup?.countryOfWorking || hingeLookup?.country || ""
            );
        }
    }, [isSecurityEditContext, editData, dtvid, hingeLookup, form1.country, form1.attuid, lookupSecExistFlag, isUS, canLockSecurityForFte]);

    const dtvPrefillHeadingOverride = useMemo(() => {
        if (isEditRoute) return "";
        if (!dtvPrefillExisting) return "";
        const statusKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_-]/g, "");
        const k = statusKey(hingeLookup?.personStatus);
        if (k === "inactive") return "Rehire Onboarding";
        if (k === "active") return "Modify Onboarding";
        return "";
    }, [isEditRoute, dtvPrefillExisting, hingeLookup?.personStatus]);

    const isSecurityEditRecord = Boolean((isEditRoute || dtvPrefillExisting) && canLockSecurityForFte);
    const isSecurityLocked = isSecurityEditRecord && !securityEditEnabled;
    const showSecurityEditButton = isSecurityEditRecord && !securityEditEnabled;
    const showSecurityDisableEditButton = isSecurityEditRecord && securityEditEnabled;

    // When DTV prefill indicates an existing person, populate last-4 into the digits state (only if blank).
    useEffect(() => {
        if (!dtvPrefillExisting || !canLockSecurityForFte) return;
        const last4 = extractDigits(
            hingeLookup?.ssnLast4d || hingeLookup?.ssn_last4d || hingeLookup?.ssnLast4 || hingeLookup?.ssn_last4 || ""
        );
        setForm1((p) => {
            if (extractDigits(p.securityId, isUS) || extractDigits(p.validateSecurity, isUS)) return p;
            if (!last4) return p;
            return { ...p, securityId: last4, validateSecurity: last4 };
        });
        setShowSecurityId(false);
        setShowConfirmSecurityId(false);
        setSecurityEditEnabled(false);
    }, [dtvPrefillExisting, hingeLookup, canLockSecurityForFte, isUS]);

    useEffect(() => {
        if (!isSecurityLocked) return;
        setFieldErrors((prev) => ({
            ...(prev || {}),
            securityId: "",
            validateSecurity: "",
        }));
    }, [isSecurityLocked]);

    useEffect(() => {
        if (!isSecurityEditContext) return;
        if (lookupSecExistFlag !== "no") return;
        if (securityTouchedRef.current || confirmSecurityTouchedRef.current) return;

        const hasSeededSecurity = Boolean(
            extractDigits(form1.securityId, isUS) || extractDigits(form1.validateSecurity, isUS)
        );
        if (!hasSeededSecurity) return;

        setShowSecurityId(false);
        setShowConfirmSecurityId(false);
        setFieldErrors((prev) => ({
            ...(prev || {}),
            securityId: "",
            validateSecurity: "",
        }));
        setForm1((prev) => ({
            ...prev,
            securityId: "",
            validateSecurity: "",
        }));
    }, [isSecurityEditContext, lookupSecExistFlag, form1.securityId, form1.validateSecurity, isUS]);

    const securityPlaceholder = useMemo(() => (isUS ? US_TEMPLATE : NONUS_TEMPLATE), [isUS]);
    const securityHint = useMemo(() => {
        return isUS
            ? "Enter SSN (9 digits). Format: xxx-xx-xxxx"
            : "Enter Security ID in f-mm-dd-xxxx format (Birth Month-Date-Last 4 of Sec ID).";
    }, [isUS]);

    const securityLabel = useMemo(() => (isUS ? "SSN" : "Security ID"), [isUS]);
    const confirmSecurityLabel = useMemo(() => (isUS ? "Confirm SSN" : "Confirm Security ID"), [isUS]);

    /* =========================
           SSN/Security ID existence check (Hinge Search)
           - Trigger: securityId has >= HINGE_SSN_MIN_DIGITS digits
           - Debounce: 2 seconds after user stops typing
           - Skips when Security ID is locked (prefilled) or URL not configured
           ========================= */
    useEffect(() => {
        if (!TB_HINGE_SEARCH_URL) return;

        if (isSecurityLocked) {
            setSsnAssociation({ status: "idle", message: "" });
            return;
        }

        const primaryErr = String(fieldErrors?.securityId || "").trim();
        const confirmErr = String(fieldErrors?.validateSecurity || "").trim();
        const hasPrimaryError = Boolean(primaryErr);
        const hasAnySecurityError = hasPrimaryError || Boolean(confirmErr);

        const digits = clampDigitsToFormat(isUS, extractDigits(form1.securityId || "", isUS));
        const min = Number.isFinite(HINGE_SSN_MIN_DIGITS) ? HINGE_SSN_MIN_DIGITS : 8;

        if (!digits || digits.length < min) {
            setSsnAssociation({ status: "idle", message: "" });
            if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);
            ssnDebounceRef.current = null;
            return;
        }

        // If the primary SSN/Security ID field is invalid, do not show availability messaging
        // and do not call Hinge search until it is valid.
        if (hasPrimaryError) {
            if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);
            ssnDebounceRef.current = null;
            setSsnAssociation({ status: "idle", message: "" });
            return;
        }

        if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);

        setSsnAssociation((prev) => (prev?.status === "found" ? prev : { status: "checking", message: "" }));

        ssnDebounceRef.current = setTimeout(async () => {
            const seq = ++ssnReqSeqRef.current;

            try {
                const roleName = String(sessionStorage.getItem("tb_role") || "").trim();
                const supplierName = String(sessionStorage.getItem("tb_supplierName") || sessionStorage.getItem("tb_vendorId") || "").trim();
                const searchDtvId = String(sessionStorage.getItem("tb_dtvid") || "").trim();

                const params = new URLSearchParams();
                params.set("q", digits);
                params.set("isActive", "true");
                /*TODO:pentest */params.set("searchMode", "validate"); // special param to indicate identity validation is needed for Hinge Search API

                if (roleName) params.set("searcherRoleName", roleName);
                if (supplierName) params.set("searcherSupplierName", supplierName);
                if (searchDtvId) params.set("searchDtvId", searchDtvId);

                const url = `${TB_HINGE_SEARCH_URL}?${params.toString()}`;
                const resp = await tbGetJson(url);

                if (seq !== ssnReqSeqRef.current) return;

                const arr = normalizeHingePeople(resp?.response ?? resp);
                if (Array.isArray(arr) && arr.length > 0) {
                    const nm = hingePersonFullName(arr[0]);
                    const msg = nm
                        ? `${securityLabel} is already associated to ${nm}.`
                        : `${securityLabel} is already associated to an existing person.`;
                    setSsnAssociation({ status: "found", message: msg });
                } else {
                    if (!isUS) {
                        const mmddMsg = validateSecurityIdMmdd8(digits);
                        if (mmddMsg) {
                            setSsnAssociation({ status: "idle", message: "" });
                            return;
                        }
                    }

                    // If there is any validation error (including confirm mismatch), do not show
                    // the green "available" message.
                    if (hasAnySecurityError) {
                        setSsnAssociation({ status: "idle", message: "" });
                        return;
                    }

                    setSsnAssociation({ status: "none", message: isUS ? "SSN is available to use." : "Security ID is available." });
                }
            } catch (e) {
                if (seq !== ssnReqSeqRef.current) return;
                console.warn("[FTE_ONBOARDING] Security ID hinge-search check failed:", e?.message || e);
                setSsnAssociation({ status: "error", message: "" });
            }
        }, 2000);

        return () => {
            if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);
            ssnDebounceRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form1.securityId, fieldErrors?.securityId, fieldErrors?.validateSecurity, isUS, isSecurityLocked, TB_HINGE_SEARCH_URL]);

    // If the Security ID rule changes while editable (country change), revalidate so the user
    // sees the correct 8 vs 9 digit requirement immediately.
    useEffect(() => {
        if (isSecurityLocked) return;
        if (form1.securityId) runFieldValidation("securityId", form1.securityId);
        if (form1.validateSecurity) runFieldValidation("validateSecurity", form1.validateSecurity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUS, isSecurityLocked]);

    // Keep mismatch validation in sync: changing either field should revalidate both.
    useEffect(() => {
        if (isSecurityLocked) return;

        const hasSecurity = (form1.securityId || "").length > 0;
        const hasConfirm = (form1.validateSecurity || "").length > 0;
        const touched = securityTouchedRef.current || confirmSecurityTouchedRef.current;

        if (hasSecurity || hasConfirm || touched) {
            runFieldValidation("securityId", form1.securityId || "");
            runFieldValidation("validateSecurity", form1.validateSecurity || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form1.securityId, form1.validateSecurity, isUS, isSecurityLocked]);

    const handleEnableSecurityEdit = () => {
        setSecurityEditEnabled(true);
        setFieldErrors((prev) => ({
            ...(prev || {}),
            securityId: "",
            validateSecurity: "",
        }));

        const nextSecurityId = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
        const nextValidateSecurity = clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS));

        // Only clear if we only have last-4 (or fewer) digits. If the rule changed while locked,
        // keep what we have and clamp 9->8 rather than wiping.
        if (nextSecurityId && nextSecurityId.length <= 4) {
            setForm1((p) => ({ ...p, securityId: "", validateSecurity: "" }));
            setShowSecurityId(false);
            setShowConfirmSecurityId(false);
        } else {
            if (nextSecurityId !== form1.securityId || nextValidateSecurity !== form1.validateSecurity) {
                setForm1((p) => ({ ...p, securityId: nextSecurityId, validateSecurity: nextValidateSecurity }));
            }
            setShowSecurityId(false);
            setShowConfirmSecurityId(false);
        }

        queueMicrotask(() => {
            try { securityRef.current?.focus(); } catch { }
        });
    };

    const handleDisableSecurityEdit = () => {
        setSecurityEditEnabled(false);
        setShowSecurityId(false);
        setShowConfirmSecurityId(false);
        setFieldErrors((prev) => ({
            ...(prev || {}),
            securityId: "",
            validateSecurity: "",
        }));
        setForm1((p) => ({
            ...p,
            securityId: securityInitialDigits || "",
            validateSecurity: securityInitialDigits || "",
        }));
    };

    // display values (masked/unmasked)
    const securityDisplayValue = useMemo(() => {
        const d = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
        if (isSecurityLocked) {
            const max = maxDigitsFor(isUS);
            if (!d) return "*".repeat(max);
            if (d.length <= 4) return "*".repeat(Math.max(0, max - d.length)) + d;
            return buildUnmaskedFormatted(isUS, d);
        }
        if (!d) return "";
        return showSecurityId ? buildUnmaskedFormatted(isUS, d) : buildMaskedDisplay(isUS, d);
    }, [isUS, form1.securityId, showSecurityId, isSecurityLocked]);

    const confirmDisplayValue = useMemo(() => {
        const d = clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS));
        if (isSecurityLocked) {
            const max = maxDigitsFor(isUS);
            if (!d) return "*".repeat(max);
            if (d.length <= 4) return "*".repeat(Math.max(0, max - d.length)) + d;
            return buildUnmaskedFormatted(isUS, d);
        }
        if (!d) return "";
        return showConfirmSecurityId
            ? buildUnmaskedFormatted(isUS, d)
            : buildMaskedDisplay(isUS, d);
    }, [isUS, form1.validateSecurity, showConfirmSecurityId, isSecurityLocked]);


    // If we're on the NEW onboarding route (no :id and no external npwId),
    // force-create mode by clearing any previous edit context + cached draft IDs.
    useEffect(() => {
        const token = (routeId || "").toString().trim();
        const isNewRoute = (!token || token.toLowerCase() === "new" || /^(?:dtvid|DTVID)\s*=/.test(token)) && !npwId;
        if (!isNewRoute) return;

        // Special case: for FTE Nominator, when /onboarding/new?dtvid=... is used,
        // we want a single Draft per dtvid and to keep editing it.
        if (dtvid) return;

        setEditData(null);
        setDraftNpwId(null);
        setDraftClientRequestId(null);
        try {
            sessionStorage.removeItem("tb_draft_npw_id");
            sessionStorage.removeItem("tb_draft_client_request_id");
        } catch { }

        setStep(1);
        setForm1(initialVal);
        setFieldErrors({});

        setSelectedCountry(null);
        setSelectedState(null);
        setSelectedCity(null);
        setSelectedTz(null);
        setCtz(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId, npwId, dtvid]);

    // Single-draft-per-dtvid: if we land on /onboarding/new?dtvid=XYZ with no NPW id,
    // redirect into the persisted Draft record for that dtvid.
    useEffect(() => {
        if (!dtvid) return;
        if (effectiveNpwId) return;

        const existing = getPersistedDraftNpwIdForDtvid(dtvid);
        if (existing && String(existing).trim()) {
            navigate(
                `/onboarding/new/${encodeURIComponent(String(existing))}?dtvid=${encodeURIComponent(dtvid)}`,
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dtvid, effectiveNpwId]);



    /* ------------ VALIDATION ------------ */
    const FIELD_LABELS = {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email",
        phone: "Phone",
        citizenship: "Country of Citizenship",
        country: "Country",
        state: "State",
        city: "City",
        timeZone: "Time Zone",
        occupancyCode: "Occupancy",
        securityId: isUS ? "SSN" : "Security ID",
        validateSecurity: isUS ? "Confirm SSN" : "Confirm Security ID",
        startDate: "Start Date",
        endDate: "End Date",
        workerTypeTax: "Worker Type (W2 / 1099 / International)",
        nonBillable: "Billable (Yes / No)",
        attRoleKey: "ATT Role",
    };

    const requiredError = (name) => {
        const label = FIELD_LABELS[name] || name;
        return `Please enter ${label}`;
    };

    const runFieldValidation = (name, value) => {
        let msg = "";
        const v = typeof value === "string" ? value.trim() : value;

        switch (name) {
            case "firstName":
            case "lastName": {
                if (v && !FIRST_LAST_NAME_REGEX.test(v)) {
                    msg = `${FIELD_LABELS[name] || name} must be 2-50 characters, start with a letter, and only contain letters, spaces, apostrophe ('), hyphen (-), or period (.)`;
                }
                break;
            }
            case "email":
                if (v) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(v)) msg = "Invalid email";
                }
                break;
            case "phone": {
                const raw = String(value || "");
                if (!raw) break;
                const isUsPhone = isUnitedStatesSelected(form1.country);
                if (isUsPhone) {
                    if (raw.length >= 1 && !/^[2-9]/.test(raw)) {
                        msg = "Invalid phone. 1st digit must be 2-9.";
                        break;
                    }
                    if (raw.length >= 4 && !/^[2-9][0-9]{2}[2-9]/.test(raw)) {
                        msg = "Invalid phone. 4th digit must be 2-9.";
                        break;
                    }
                    if (raw.length !== 10) {
                        msg = "Phone must be 10 digits";
                        break;
                    }
                    if (!/^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(raw)) msg = "Invalid phone. 1st & 4th digits must be 2-9.";
                    break;
                }

                if (!/^\d+$/.test(raw)) {
                    msg = "Phone must contain digits only (no spaces or special characters).";
                    break;
                }
                if (raw.length < 10) {
                    msg = "Phone must be at least 10 digits.";
                    break;
                }
                if (raw.length > 15) {
                    msg = "Phone must be 15 digits or less";
                }
                break;
            }

            case "securityId": {
                if (isSecurityLocked) break;
                const digits = clampDigitsToFormat(isUS, extractDigits(v, isUS));
                if (!digits) msg = requiredError("securityId");
                else if (digits.length !== maxDigitsFor(isUS))
                    msg = isUS ? "SSN must be 9 digits" : "Security ID must be 8 digits";
                else if (!isUS) {
                    const mmddMsg = validateSecurityIdMmdd8(digits);
                    if (mmddMsg) msg = mmddMsg;
                }
                break;
            }

            case "validateSecurity": {
                if (isSecurityLocked) break;
                const d1 = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
                const d2 = clampDigitsToFormat(isUS, extractDigits(v, isUS));
                if (!d2) msg = requiredError("validateSecurity");
                else if (d2.length !== maxDigitsFor(isUS))
                    msg = isUS
                        ? "Confirm SSN must be 9 digits"
                        : "Confirm Security ID must be 8 digits";
                else {
                    if (!isUS) {
                        const mmddMsg = validateSecurityIdMmdd8(d2);
                        if (mmddMsg) {
                            msg = mmddMsg;
                            break;
                        }
                    }
                    if (d1 && d2 !== d1) msg = isUS ? "Confirm SSN must match SSN" : "Confirm Security ID must match Security ID";
                }
                break;
            }

            case "startDate":
            case "endDate":
                if (!v) msg = requiredError(name);
                break;
            case "citizenship":
            case "country":
            case "occupancyCode":
            case "workerTypeTax":
            case "nonBillable":
            case "attRoleKey":
                if (!v) msg = requiredError(name);
                break;
            case "state":
                if (isStateRequired && !v) msg = requiredError(name);
                break;
            case "city":
                if (isCityRequired && !v) msg = requiredError(name);
                break;
            default:
                break;
        }

        setFieldErrors((prev) => ({ ...prev, [name]: msg }));
        return msg;
    };

    const REQUIRED_FIELDS_BASE = [
        "firstName",
        "lastName",
        "email",
        "citizenship",
        "country",
        "occupancyCode",
        "startDate",
        "endDate",
        "workerTypeTax",
        "nonBillable",
        "attRoleKey",
    ];

    const getFieldValue = (name) => {
        switch (name) {
            case "workerTypeTax":
                return workerTypeTax;
            case "nonBillable":
                return nonBillable;
            default:
                return form1[name];
        }
    };

    const validateAll = () => {
        if (/^\d+$/.test(String(effectiveNpwId || "").trim()) && !lookupSecExistFlag && String(editData?.dtvId || editData?.dtv_id || form1.dtvId || dtvid || "").trim()) {
            toast.error("Loading Security ID details. Please wait a moment.", { toastId: "onb-security-lookup-pending" });
            return false;
        }

        const newErrors = {};

        const required = [...REQUIRED_FIELDS_BASE];
        if (isStateRequired) required.push("state");
        if (isCityRequired) required.push("city");
        if (!isSecurityLocked) {
            required.push("securityId", "validateSecurity");
        }

        required.forEach((f) => {
            const value = getFieldValue(f);
            if (
                value === null ||
                value === undefined ||
                (typeof value === "string" && !value.trim())
            ) {
                newErrors[f] = requiredError(f);
            } else {
                const msg = runFieldValidation(f, value);
                if (msg) newErrors[f] = msg;
            }
        });

        if (!isStateRequired) newErrors.state = "";
        if (!isCityRequired) newErrors.city = "";

        const phoneVal = String(form1.phone || "").trim();
        if (phoneVal) {
            const phoneMsg = runFieldValidation("phone", phoneVal);
            if (phoneMsg) newErrors.phone = phoneMsg;
        }

        if (form1.startDate && form1.endDate) {
            if (form1.endDate < form1.startDate) {
                newErrors.endDate = "End Date cannot be before Start Date";
            }
        }

        setFieldErrors((prev) => ({ ...prev, ...newErrors }));
        const hasError = Object.values(newErrors).some((m) => m);
        if (hasError) toast.error("Please fix the highlighted errors before continuing.", { toastId: "onb-fix-errors" });
        return !hasError;
    };

    /* DATE LIMITS */
    const today = useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);
    const minStart = useMemo(() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        return d;
    }, [today]);
    const maxStart = useMemo(() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() + 1);
        return d;
    }, [today]);
    const minEnd = useMemo(
        () =>
            form1.startDate
                ? new Date(Math.max(form1.startDate.getTime(), today.getTime()))
                : today,
        [form1.startDate, today]
    );

    /* LOAD ROLE CATALOG — one shot + sessionStorage cache */
    useEffect(() => {
        if (roleCatalogFetchedRef.current) return;
        roleCatalogFetchedRef.current = true;

        const CACHE_KEY = "npw.roleCatalog.v1";
        const CACHE_TTL_MS = 15 * 60 * 1000;

        const cachedRaw = sessionStorage.getItem(CACHE_KEY);
        if (cachedRaw) {
            try {
                const parsed = JSON.parse(cachedRaw);
                if (parsed?.at && Array.isArray(parsed.data)) {
                    if (Date.now() - parsed.at < CACHE_TTL_MS) {
                        setHingeRoleCatalog(parsed.data);
                        setDataLoads((prev) => ({
                            ...prev,
                            roles: { ...prev.roles, field: "Role", status: "success" }
                        }));
                        return;
                    }
                }
            } catch { }
        }

        const ac = new AbortController();
        (async () => {
            try {
                setDataLoads((prev) => ({
                    ...prev,
                    roles: {
                        field: "Role",
                        status: "loading",
                        startedAt:
                            prev?.roles?.status === "loading" && prev?.roles?.startedAt
                                ? prev.roles.startedAt
                                : Date.now()
                    }
                }));
                const r = await tbGetJson(TB_HINGE_ROLE_LIST_URL, { fetchInit: { signal: ac.signal } });
                const payload = r?.response && typeof r.response === "object" ? r.response : r;
                const catalog = Array.isArray(payload?.roles)
                    ? payload.roles
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : Array.isArray(payload)
                            ? payload
                            : [];
                setHingeRoleCatalog(catalog);
                sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: catalog }));

                setDataLoads((prev) => ({
                    ...prev,
                    roles: { ...prev.roles, field: "Role", status: "success" }
                }));
            } catch (e) {
                console.warn("Role catalog load failed", e);
                setDataLoads((prev) => ({
                    ...prev,
                    roles: { ...prev.roles, field: "Role", status: "error" }
                }));
            }
        })();

        return () => ac.abort();
    }, []);

    const attRoleSelectOptions = useMemo(() => {
        const cpRoleMap = new Map();
        (hingeRoleCatalog || []).forEach((role) => {
            const { dtvRoleId, dtvRoleName } = role || {};
            (role?.cinglepointRoles || []).forEach((cp) => {
                const cpRoleId = cp?.cpRoleId || "";
                if (!cpRoleId) return;
                if (!cpRoleMap.has(cpRoleId)) {
                    cpRoleMap.set(cpRoleId, {
                        cpRoleId,
                        cpRoleDesc: cp?.cpRoleDesc || "",
                        dtvRoleId,
                        dtvRoleName,
                    });
                }
            });
        });
        return Array.from(cpRoleMap.values()).map((o) => ({
            value: o.cpRoleId,
            label: `${o.cpRoleId}${o.cpRoleDesc ? " - " + o.cpRoleDesc : ""}`,
            meta: o,
        }));
    }, [hingeRoleCatalog]);

    const selectedAttRoleOption =
        attRoleSelectOptions.find((o) => o.value === form1.attRoleKey) || null;

    /* GEO INIT */
    useEffect(() => {
        setCountryOptions(
            Country.getAllCountries().map((c) => ({ label: c.name, value: c.isoCode }))
        );
    }, []);


    /* Country of Citizenship list (CP) */
    useEffect(() => {
        const url = String(CP_COUNTRYLIST_URL || "").trim();
        if (!url) return;

        const ac = new AbortController();
        let alive = true;

        (async () => {
            try {
                const r = await tbGetJson(url, { fetchInit: { signal: ac.signal } });
                const payload = r?.response && typeof r.response === "object" ? r.response : r;

                const list = Array.isArray(payload?.citizenships)
                    ? payload.citizenships
                    : Array.isArray(payload?.rows)
                        ? payload.rows
                        : Array.isArray(payload)
                            ? payload
                            : [];

                const opts = (list || [])
                    .map((x) => (x && typeof x === "object" ? (x.citizenship_value || x.citizenship || x.value || x.label) : x))
                    .filter((v) => v != null && String(v).trim())
                    .map((v) => {
                        const s = String(v).trim();
                        return { label: s, value: s };
                    });

                if (alive && opts.length) setCitizenshipOptions(opts);
            } catch (e) {
                console.warn("CP country-of-citizenship load failed", e);
            }
        })();

        return () => {
            alive = false;
            ac.abort();
        };
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            const s = State.getStatesOfCountry(selectedCountry.value).map((x) => ({
                label: x.name,
                value: x.isoCode,
            }));
            setStateOptions(s);
            // During edit prefill, we set the country programmatically and we do NOT want
            // to wipe state/city/security id.
            if (suppressCountryCascadeRef.current) {
                suppressCountryCascadeRef.current = false;
                return;
            }

            setSelectedState(null);
            setSelectedCity(null);

            const label = selectedCountry.label;
            setForm1((p) => ({
                ...p,
                country: label,
                state: "",
                city: "",
            }));

            setShowSecurityId(false);
            setShowConfirmSecurityId(false);

            setFieldErrors((prev) => ({ ...prev, securityId: "", validateSecurity: "" }));
        } else setStateOptions([]);
    }, [selectedCountry]); // eslint-disable-line

    useEffect(() => {
        const phoneVal = String(form1.phone || "").trim();
        if (!phoneVal) {
            setFieldErrors((prev) => ({ ...prev, phone: "" }));
            return;
        }
        runFieldValidation("phone", phoneVal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form1.country]);

    useEffect(() => {
        if (selectedCountry && selectedState) {
            const ct = City.getCitiesOfState(selectedCountry.value, selectedState.value).map((c) => ({
                label: c.name,
                value: c.name,
            }));
            setCityOptions(ct);
            // During edit prefill, we set the state programmatically and we do NOT want
            // to wipe the city.
            if (suppressStateCascadeRef.current) {
                suppressStateCascadeRef.current = false;
                return;
            }

            setSelectedCity(null);
            setForm1((p) => ({
                ...p,
                state: selectedState.label,
                city: "",
            }));
        } else setCityOptions([]);
    }, [selectedState, selectedCountry]);

    const handleCity = (opt) => {
        setSelectedCity(opt);
        const label = opt?.label || "";
        setForm1((p) => ({
            ...p,
            city: label,
            state: selectedState?.label || "",
            country: selectedCountry?.label || "",
        }));
        runFieldValidation("city", label);
    };

    /* MERGE HINGE */
    const mergeIfBlank = (cur, hinge) => (!cur || cur === "" ? hinge : cur);

    const normalizeWorkerTypeTax = (v) => {
        const s = String(v || "").trim();
        if (!s) return "";
        const up = s.toUpperCase().replace(/\s+/g, "");
        if (up === "W2" || up === "W-2") return "W-2";
        if (up === "1099") return "1099";
        if (up === "INTERNATIONAL") return "International";
        return s;
    };
    const normalizeYesNo = (v) => {
        const s = String(v || "").trim().toLowerCase();
        if (!s) return "";
        if (["y", "yes", "true", "1"].includes(s)) return "Yes";
        if (["n", "no", "false", "0"].includes(s)) return "No";
        return String(v || "").trim();
    };

    const applyHinge = (h) => {
        const hingeWorkerTypeTax = normalizeWorkerTypeTax(h?.w2_1099);
        const hingeNonBillable = normalizeYesNo(h?.nonBillableIndicator);
        // Worker/NPW type comes from lookup as `personType`.
        const hingeNpwType = String(h?.personType || h?.employment_type || h?.npw_type || "").trim();
        const hingeCountry = h.countryOfWorking || h.country;
        const phoneMaxLen = isUnitedStatesSelected(hingeCountry) ? 10 : 15;

        let citizenship = mergeIfBlank(form1.citizenship, h.countryOfCitizenship);
        let countryWorking = mergeIfBlank(form1.country, h.countryOfWorking || h.country);

        /*Patch: handled country data issue for USA */
        if ((citizenship === 'USA' && !countryWorking) || countryWorking === 'USA') {
            countryWorking = 'United States';
        }
        if (citizenship === 'USA') {
            citizenship = 'United States of America (USA) Citizen';
        }

        setForm1((p) => ({
            ...p,
            attuid: mergeIfBlank(p.attuid, h.attuid || h.attUid || h.attUID || ""),
            // FTE preload: supervisorDtvAnchorId is sent as mgrDtvId for manager validation.
            webphoneManagerId: mergeIfBlank(p.webphoneManagerId, getLookupSupervisorDtvAnchorId(h)),
            webphone_manager_attuid: null,
            firstName: mergeIfBlank(p.firstName, h.firstName),
            lastName: mergeIfBlank(p.lastName, h.lastName),
            email: mergeIfBlank(p.email, h.secondaryEmail || h.primaryEmail),
            phone: mergeIfBlank(p.phone, h.phone?.replace(/\D/g, "").slice(0, phoneMaxLen)),
            citizenship: citizenship,
            // Lookup payloads use countryOfWorking; DB payloads typically use country.
            country: countryWorking,
            state: mergeIfBlank(p.state, h.state),
            city: mergeIfBlank(p.city, h.city),
            timeZone: mergeIfBlank(p.timeZone, h.timeZone),
            occupancyCode: mergeIfBlank(
                p.occupancyCode,
                (h.occupancyIndicator || h.occupancy || "").toLowerCase()
            ),
            startDate: mergeIfBlank(
                p.startDate,
                h.effectiveStartDate ? parseAnyDate(h.effectiveStartDate) : null
            ),
            endDate: mergeIfBlank(
                p.endDate,
                h.effectiveEndDate ? parseAnyDate(h.effectiveEndDate) : null
            ),
            workerTypeTax: mergeIfBlank(p.workerTypeTax, hingeWorkerTypeTax),
            nonBillable: mergeIfBlank(p.nonBillable, hingeNonBillable),
            // NPW/Worker type from lookup (personType); override the default placeholder.
            npwType:
                p.npwType && p.npwType !== "Non-staff Supp Worker"
                    ? p.npwType
                    : (hingeNpwType || p.npwType),
            npwTypeRaw: p.npwTypeRaw || hingeNpwType || null,
            attRoleKey: mergeIfBlank(p.attRoleKey, h.cinglepointRoleId || ""),
            attRoleDtvRoleId: mergeIfBlank(p.attRoleDtvRoleId, h.dtvRoleId),
            attRoleDtvRoleName: mergeIfBlank(p.attRoleDtvRoleName, h.dtvRoleName),
            attRoleCpRoleId: mergeIfBlank(p.attRoleCpRoleId, h.cinglepointRoleId),
            attRoleCpRoleDesc: mergeIfBlank(p.attRoleCpRoleDesc, h.cinglepointRoleDesc || h.dtvRoleName),
            attRoleCpProfileId: "",
            attRoleCpProfileDesc: "",
        }));

        if (hingeWorkerTypeTax) {
            setWorkerTypeTax((prev) => (String(prev || "").trim() ? prev : hingeWorkerTypeTax));
        }
        if (hingeNonBillable) {
            setNonBillable((prev) => (String(prev || "").trim() ? prev : hingeNonBillable));
        }

        if (h.timeZone) {
            const tz =
                timeZoneOptions.find((x) => x.label.includes(h.timeZone) || x.value === h.timeZone) || null;
            if (tz) setSelectedTz(tz);
        }
        if (citizenship) setCtz({ label: citizenship, value: citizenship });
    };

    // DTVID prefill: when hinge sets plain strings (form1.country/state/city),
    // also sync the React-Select values so dropdowns display.
    useEffect(() => {
        if (!dtvid) return;
        if (editData) return;
        const c = String(form1.country || "").trim();
        if (!c) return;
        if (selectedCountry && String(selectedCountry.label || "").trim() === c) return;
        const cOpt = (countryOptions || []).find((x) => String(x.label || "").trim() === c);
        if (cOpt) {
            suppressCountryCascadeRef.current = true;
            setSelectedCountry(cOpt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dtvid, editData, form1.country, countryOptions, selectedCountry]);

    useEffect(() => {
        if (!dtvid) return;
        if (editData) return;
        if (!selectedCountry) return;
        const s = String(form1.state || "").trim();
        if (!s) return;
        if (selectedState && String(selectedState.label || "").trim() === s) return;
        const sOpt = (stateOptions || []).find((x) => String(x.label || "").trim() === s);
        if (sOpt) {
            suppressStateCascadeRef.current = true;
            setSelectedState(sOpt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dtvid, editData, selectedCountry, form1.state, stateOptions, selectedState]);

    useEffect(() => {
        if (!dtvid) return;
        if (editData) return;
        if (!selectedCountry || !selectedState) return;
        const c = String(form1.city || "").trim();
        if (!c) return;
        if (selectedCity && String(selectedCity.label || "").trim() === c) return;
        if (!cityOptions || !cityOptions.length) return;
        const cOpt = (cityOptions || []).find((x) => String(x.label || "").trim() === c);
        if (cOpt) setSelectedCity(cOpt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dtvid, editData, selectedCountry, selectedState, form1.city, cityOptions, selectedCity]);

    /* LOAD HINGE + EDIT DATA — one shot per (dtvid,npwId) combo */
    useEffect(() => {
        if (!prefillKey) return;
        if (prefillFetchedKeysRef.current.has(prefillKey)) {
            setLoading(false);
            return;
        }
        prefillFetchedKeysRef.current.add(prefillKey);

        const ac = new AbortController();
        (async () => {
            try {
                if (dtvid) {
                    const hingeUrl = `${TB_HINGE_PERSON_LOOKUP}?dtvId=${encodeURIComponent(dtvid)}`;
                    const hingeResp = await tbGetJson(hingeUrl, { fetchInit: { signal: ac.signal } });
                    if (hingeResp) {
                        setLookupSecExistFlag(getLookupSecurityExistenceFromPayload(hingeResp));
                        // TB proxy returns wrapper: { requestId, timing, response: {...} }
                        // Some callers wrap again under .data.
                        const unwrapLookupPayload = (r) => {
                            let p = r;
                            for (let i = 0; i < 3; i++) {
                                if (p && typeof p === "object" && Object.prototype.hasOwnProperty.call(p, "data") && p.data && typeof p.data === "object") {
                                    p = p.data;
                                    continue;
                                }
                                if (p && typeof p === "object" && Object.prototype.hasOwnProperty.call(p, "response")) {
                                    const rr = p.response;
                                    if (typeof rr === "string") {
                                        try {
                                            p = JSON.parse(rr);
                                            continue;
                                        } catch {
                                            // ignore
                                        }
                                    }
                                    if (rr && typeof rr === "object") {
                                        p = rr;
                                        continue;
                                    }
                                }
                                break;
                            }
                            return p;
                        };

                        const payload = unwrapLookupPayload(hingeResp);
                        const person =
                            payload?.personList?.[0] ||
                            payload?.person?.[0] ||
                            payload?.person ||
                            payload?.data?.[0] ||
                            payload?.items?.[0] ||
                            (payload && (payload.firstName || payload.lastName || payload.dtvAnchorId || payload.primaryEmail || payload.attuid || payload.attUid || payload.attUID) ? payload : null) ||
                            (Array.isArray(payload) ? payload[0] : null);

                        const hingePayload = person || payload;

                        setHingeLookup(hingePayload);
                        applyHinge(hingePayload);
                    }
                }
                if (effectiveNpwId) {
                    const resp = await apiFetchById(effectiveNpwId);
                    const d = resp?.data || resp;
                    if (d) {
                        const mapped = {
                            ...initialVal,
                            ...mapApiToForm1(d),
                        };
                        setLookupSecExistFlag(normalizeLookupSecurityExistence(mapped?.isSecExist));
                        setForm1(mapped);
                        setWorkerTypeTax(mapped.workerTypeTax || "");
                        setNonBillable(mapped.nonBillable || "");

                        // Store the full edit payload (like other roles), plus meta used by this page.
                        setEditData({
                            ...mapped,
                            status: d.status || "Draft",
                            npw_id: d.npw_id || effectiveNpwId,
                            client_request_id: d.client_request_id || null,
                        });

                        const effectiveNpw = String(d.npw_id || effectiveNpwId || "");
                        if (effectiveNpw) {
                            setDraftNpwId(effectiveNpw);
                            sessionStorage.setItem("tb_draft_npw_id", effectiveNpw);
                            if (dtvid) setPersistedDraftNpwIdForDtvid(dtvid, effectiveNpw);
                        }
                        if (d.client_request_id) {
                            setDraftClientRequestId(String(d.client_request_id));
                            sessionStorage.setItem("tb_draft_client_request_id", String(d.client_request_id));
                        }
                    }
                }
            } catch (e) {
                console.warn("Prefill load failed", e);
            } finally {
                setLoading(false);
            }
        })();

        return () => ac.abort();
    }, [prefillKey]); // eslint-disable-line

    const editLoadSearchableDDval = (val, optn, setFn) => {
        const v = String(val || "").trim();
        if (!v) return;
        const match = (optn || []).find((c) => c.label === v || c.value === v);
        setFn(match || null);
    };

    // After edit data loads, sync React-Select values (country/state/city/tz/citizenship)
    // and simple dropdowns (workerTypeTax/nonBillable), matching other onboarding pages.
    useEffect(() => {
        if (!editData) return;

        // These two are stored outside form1
        if (editData.workerTypeTax && workerTypeTax !== editData.workerTypeTax) {
            setWorkerTypeTax(editData.workerTypeTax);
        }
        if (editData.nonBillable && nonBillable !== editData.nonBillable) {
            setNonBillable(editData.nonBillable);
        }

        if (editData.country) {
            const cOpt = (countryOptions || []).find((c) => c.label === editData.country);
            // Only set if not set, or matches editData
            if (cOpt && (!selectedCountry || selectedCountry.label === editData.country)) {
                suppressCountryCascadeRef.current = true;
                setSelectedCountry(cOpt);
            }
        }

        // Only set state from editData when editData or stateOptions change
        if (editData.state) {
            editLoadSearchableDDval(editData.state, stateOptions, (opt) => {
                if (opt) {
                    suppressStateCascadeRef.current = true;
                    setSelectedState(opt);
                }
            });
        }

        if (editData.citizenship) {
            setCtz({ label: String(editData.citizenship), value: String(editData.citizenship) });
        }

        if (editData.timeZone) {
            editLoadSearchableDDval(editData.timeZone, timeZoneOptions, setSelectedTz);
        }
    }, [editData, countryOptions, selectedCountry, stateOptions, timeZoneOptions, workerTypeTax, nonBillable]);

    useEffect(() => {
        if (editData && selectedCountry && selectedState && cityOptions.length) {
            if (editData.city) {
                editLoadSearchableDDval(editData.city, cityOptions, setSelectedCity);
            }
        }
    }, [editData, selectedCountry, selectedState, cityOptions]);

    /* =========================
       Security ID input handlers
       ========================= */

    const handleSecKeyDown = (which /* "securityId" | "validateSecurity" */) => (e) => {
        if (isSecurityLocked) { e.preventDefault(); return; }
        const key = e.key;

        // Allow paste (handled in onPaste). Do not preventDefault here;
        if ((e.ctrlKey || e.metaKey)) {
            if (["v", "a", "c", "x"].includes(String(key || "").toLowerCase())) return;
        }

        const curDigits = clampDigitsToFormat(isUS, extractDigits(form1[which], isUS));
        const max = maxDigitsFor(isUS);
        const pos = digitPosFor(isUS);

        if (key === "Tab") return;

        if (key === "Backspace") {
            e.preventDefault();
            if (which === "securityId") securityTouchedRef.current = true;
            if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;
            const next = curDigits.slice(0, -1);
            setForm1((p) => ({ ...p, [which]: next }));
            runFieldValidation(which, next);

            queueMicrotask(() => {
                const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
                if (!next.length) setCaretToSlotIndex(el, 0);
                else setCaretToNextSlot(el, isUS, next.length);
            });
            return;
        }

        // SSN (US): 9 digits only; Security ID (non-US): MMDDXXXX (first 4 digits, last 4 alphanumeric)
        const isValidKey = (key, pos, isUS) => {
            if (isUS) {
                // SSN: only digits, max 9
                return /^[0-9]$/.test(key) && pos < 9;
            } else {
                // Security ID: MMDDXXXX
                if (pos < 4) return /^[0-9]$/.test(key); // MMDD
                if (pos >= 4 && pos < 8) return /^[A-Za-z0-9]$/.test(key); // XXXX
                return false;
            }
        };


        if (isValidKey(key, curDigits.length, isUS)) {
            e.preventDefault();
            if (which === "securityId") securityTouchedRef.current = true;
            if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;
            if (curDigits.length >= max) return;

            const next = curDigits + key;
            setForm1((p) => ({ ...p, [which]: next }));
            runFieldValidation(which, next);

            queueMicrotask(() => {
                const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
                if (el) {
                    if (next.length >= max) {
                        setCaretToSlotIndex(el, templateFor(isUS).length);
                    } else {
                        const nextSlot = pos[next.length] ?? templateFor(isUS).length;
                        setCaretToSlotIndex(el, nextSlot);
                    }
                }
            });
            return;
        }

        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(key)) {
            e.preventDefault();
            const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
            queueMicrotask(() => {
                if (!curDigits.length) setCaretToSlotIndex(el, 0);
                else setCaretToNextSlot(el, isUS, curDigits.length);
            });
            return;
        }

        e.preventDefault();
    };

    const handleSecChange = (which /* "securityId" | "validateSecurity" */) => (e) => {
        if (isSecurityLocked) return;
        const raw = e?.target?.value ?? "";
        const digits = clampDigitsToFormat(isUS, extractDigits(raw, isUS));
        if (which === "securityId") securityTouchedRef.current = true;
        if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;
        setForm1((p) => ({ ...p, [which]: digits }));
        runFieldValidation(which, digits);
    };

    const handleSecPaste = (which) => (e) => {
        if (isSecurityLocked) { e.preventDefault(); return; }
        e.preventDefault();
        const clip = e.clipboardData?.getData("text") || "";
        const digits = clampDigitsToFormat(isUS, extractDigits(clip, isUS));
        if (!digits) return;

        if (which === "securityId") securityTouchedRef.current = true;
        if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;

        setForm1((p) => ({ ...p, [which]: digits }));
        runFieldValidation(which, digits);

        queueMicrotask(() => {
            const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
            setCaretToSlotIndex(el, templateFor(isUS).length);
        });
    };

    const handleSecFocus = (which) => () => {
        if (isSecurityLocked) return;
        const digits = clampDigitsToFormat(isUS, extractDigits(form1[which], isUS));
        const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;

        if (!digits) {
            queueMicrotask(() => setCaretToSlotIndex(el, 0));
            return;
        }
        queueMicrotask(() => setCaretToNextSlot(el, isUS, digits.length));
    };

    // Map API response to form1 structure for troubleshooting
    function mapApiToForm1(apiData) {
        if (!apiData) return {};
        const normalizeWorkerTypeTax = (v) => {
            const s = String(v || "").trim();
            if (!s) return "";
            const up = s.toUpperCase().replace(/\s+/g, "");
            if (up === "W2" || up === "W-2") return "W-2";
            if (up === "1099") return "1099";
            if (up === "INTERNATIONAL") return "International";
            return s;
        };

        const normalizeYesNo = (v) => {
            if (v === true) return "Yes";
            if (v === false) return "No";
            const s = String(v || "").trim().toLowerCase();
            if (!s) return "";
            if (["y", "yes", "true", "1"].includes(s)) return "Yes";
            if (["n", "no", "false", "0"].includes(s)) return "No";
            // already formatted
            if (s === "yes") return "Yes";
            if (s === "no") return "No";
            return String(v || "").trim();
        };

        const asStr = (v) => (v === undefined || v === null ? "" : String(v).trim());
        const asStrNoTrim = (v) => (v === undefined || v === null ? "" : String(v));
        const secDigits = resolveSecurityDigits(apiData);
        return {
            isSecExist: normalizeLookupSecurityExistence(apiData.isSecExist),
            firstName: asStrNoTrim(apiData.first_name || apiData.firstName),
            lastName: asStrNoTrim(apiData.last_name || apiData.lastName),
            email: asStr(apiData.email),
            phone: asStr(apiData.phone),
            citizenship: asStr(apiData.citizenship),
            country: asStr(apiData.country),
            state: asStr(apiData.state),
            city: asStr(apiData.city),
            timeZone: asStr(apiData.time_zone || apiData.timeZone),
            occupancyCode: asStr(apiData.occupancy),
            securityId: secDigits,
            validateSecurity: secDigits,
            startDate: apiData.start_date ? new Date(apiData.start_date) : null,
            endDate: apiData.end_date ? new Date(apiData.end_date) : null,
            role: asStr(apiData.role || apiData.role_title || apiData.roleTitle),
            workerTypeTax: normalizeWorkerTypeTax(apiData.workerTypeTax || apiData.worker_type_tax),
            nonBillable: normalizeYesNo(apiData.nonBillable || apiData.non_billable),
            attRoleKey: asStr(
                apiData.attRoleKey ||
                apiData.att_role_key ||
                apiData.att_role_cp_role_id ||
                apiData.cinglepointRoleId
            ),
            attRoleDtvRoleId: asStr(apiData.attRoleDtvRoleId || apiData.att_role_dtv_role_id),
            attRoleDtvRoleName: asStr(apiData.attRoleDtvRoleName || apiData.att_role_dtv_role_name),
            attRoleCpRoleId: asStr(apiData.attRoleCpRoleId || apiData.att_role_cp_role_id),
            attRoleCpRoleDesc: asStr(apiData.attRoleCpRoleDesc || apiData.att_role_cp_role_desc),
            attRoleCpProfileId: asStr(apiData.attRoleCpProfileId || apiData.att_role_cp_profile_id),
            attRoleCpProfileDesc: asStr(apiData.attRoleCpProfileDesc || apiData.att_role_cp_profile_desc),
            webphone_manager_attuid: asStr(apiData.webphone_manager_attuid || apiData.webphoneManagerAttuid),
            attuid: asStr(apiData.attuid || apiData.attUid || apiData.attUID),

            // Extra fields required by backend (edit prefill)
            // /approval-details?npw_id payload uses snake_case: dtv_id, webphone_manager
            dtvId: asStr(apiData.dtvId || apiData.dtv_id || apiData.dtvAnchorId),
            webphoneManagerId: asStr(
                apiData.webphoneManagerId ||
                apiData.webphone_manager ||
                apiData.webphone_manager_id ||
                apiData.webphoneManager
            ),
        };
    }

    /* =========================
       PAYLOAD builder (NO hyphens/f, NO validateSecurity)
       ========================= */
    const buildPayload = (status, label, webphoneOverride = null) => {
        const { submitter_name, submitter_email, approver, loggedInUserRole, submitter_dtvid } =
            getSessionIdentity();

        // If lookup API returned ATTUID, include it in payload (create + edit).
        const attuidPayload = String(
            hingeLookup?.attuid ?? hingeLookup?.attUid ?? hingeLookup?.attUID ?? form1?.attuid ?? editData?.attuid ?? editData?.attUid ?? editData?.attUID ?? editData?.att_uid ?? ""
        ).trim();

        const lookupDtvAnchorId = hingeLookup?.dtvAnchorId || hingeLookup?.attuid || hingeLookup?.dtvId || "";

        // Prefer edit values from /approval-details?npw_id when present.
        const dtvAnchorId =
            String(form1?.dtvId || "").trim() ||
            String(editData?.dtv_id || editData?.dtvId || "").trim() ||
            String(lookupDtvAnchorId || "").trim() ||
            String(dtvid || "").trim() ||
            "";

        const webphoneManager = String(
            webphoneOverride?.managerId ||
            getLookupSupervisorDtvAnchorId(hingeLookup) ||
            form1?.webphoneManagerId ||
            ""
        ).trim();
        const cachedManager = readWebphoneManagerCache(webphoneManager);
        const cachedManagerAttuid = isActiveManagerAttuidResp(cachedManager?.response)
            ? getManagerAttuidFromResp(cachedManager?.response)
            : "";
        const webphoneManagerAttuid = String(
            webphoneOverride?.webphoneManagerAttuid ||
            webphoneOverride?.webphone_manager_attuid ||
            form1?.webphone_manager_attuid ||
            cachedManagerAttuid ||
            ""
        ).trim();

        const effectiveSaveNpwId = draftNpwId || (isEditRoute ? effectiveNpwId : null) || null;

        const secDigits = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));

        // FTE fix: CP Profile fields may be missing from the form state.
        // Derive the first profile for the selected CP Role from the role catalog.
        const deriveCpProfileForSelectedRole = () => {
            const cpRoleId = String(form1.attRoleCpRoleId || form1.attRoleKey || "").trim();
            if (!cpRoleId) return { cpProfileId: "", cpProfileDesc: "" };

            for (const role of hingeRoleCatalog || []) {
                for (const cp of role?.cinglepointRoles || []) {
                    if (String(cp?.cpRoleId || "").trim() !== cpRoleId) continue;
                    const profile = (cp?.cpProfiles || [])[0];
                    const cpProfileId = String(profile?.cpProfileId || "").trim();
                    const cpProfileDesc = String(profile?.cpProfileDesc || "").trim();
                    if (cpProfileId || cpProfileDesc) return { cpProfileId, cpProfileDesc };
                }
            }
            return { cpProfileId: "", cpProfileDesc: "" };
        };

        const derivedProfile = deriveCpProfileForSelectedRole();

        const form1Payload = {
            ...form1,
            workerTypeTax,
            nonBillable,
            npwType: DEFAULT_WORKER_TYPE,
            // Extra fields required by backend (keep top-level payload keys intact too)
            dtvId: dtvAnchorId,
            webphoneManagerId: webphoneManager,
            webphone_manager_attuid: webphoneManagerAttuid || null,
            startDate: serializeDate(form1.startDate),
            endDate: serializeDate(form1.endDate),
            attRoleCpProfileId: String(form1.attRoleCpProfileId || "").trim() || derivedProfile.cpProfileId,
            attRoleCpProfileDesc: String(form1.attRoleCpProfileDesc || "").trim() || derivedProfile.cpProfileDesc,
        };


        // Ensure `role` is always present (parity with other onboarding pages).
        // If the explicit role/title is blank, derive it from the selected ATT role.
        {
            const explicit = String(form1Payload.role || "").trim();
            const derived =
                String(form1Payload.attRoleDtvRoleName || "").trim() ||
                String(form1Payload.attRoleCpRoleDesc || "").trim() ||

                String(form1Payload.attRoleKey || "").trim();
            form1Payload.role = explicit || derived || "";
        }
        // UI-only field
        delete form1Payload.validateSecurity;
        // Avoid sending empty/untyped value; omit when blank
        delete form1Payload.securityId;
        if (secDigits) form1Payload.securityId = secDigits;

        // Only send Security ID/SSN when user is actually editing it, and only if it changed.
        const securityUnchanged = secDigits === securityInitialDigits;
        const shouldOmitSecurityId = isSecurityEditRecord && (!securityEditEnabled || securityUnchanged);
        if (shouldOmitSecurityId) delete form1Payload.securityId;

       /*TODO:pentest*/
        // //body v = hashed-roleSess
        const dtvRaw = sessionStorage.getItem("tb_dtvid") || "";
        const ts = Date.now();
        const vRaw = `${loggedInUserRole}_*v*_${ts}`;
        const dRaw = `${dtvRaw}_*v*_${ts}`;
        const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);
        const dParam = typeof btoa === "function" ? btoa(dRaw) : window.btoa(dRaw);

        return {
            workerType: workerTypeLabel,
            dtvAnchorId,
            webphoneManager,
            webphone_manager_attuid: webphoneManagerAttuid || null,
            ...(isReactiveAttuidFlow && status === "Submitted" ? { reactive_attuid: true } : {}),

            ...(attuidPayload ? { attuid: attuidPayload } : {}),

            submitterDtvid: submitter_dtvid || undefined,

            ...(effectiveSaveNpwId
                ? { npw_id: effectiveSaveNpwId, npwId: effectiveSaveNpwId }
                : {}),
            ...(draftClientRequestId
                ? { clientRequestId: draftClientRequestId, client_request_id: draftClientRequestId }
                : {}),

            form1: form1Payload,

            status,
            buttonAction: label || status,

            submitter: submitter_email || undefined,
            submitter_name: submitter_name || undefined,
            approver: approver || null,

            loggedInUserRole: loggedInUserRole || "",
            nominatorRole: loggedInUserRole || "",
            /*TODO:pentest*/
            v: vParam,
            d: dParam,
        };
    };

    const hasExistingNpw = () => {
        const effective = draftNpwId || effectiveNpwId || editData?.npw_id;
        return !!(effective && String(effective).trim());
    };

    const callNewOrEdit = async (payload) => {
        if (hasExistingNpw()) return apiSubmitEdit(payload);
        return apiSubmitNew(payload);
    };

    const saveDraft = async () => {
        if (!ensureIdentityOrForceSSO()) return;


        // Draft rule: require Security ID entry when Security is editable (NEW flow).
        // If editing an existing NPW record (Security locked), we do NOT block Draft.
        if (!isSecurityLocked) {
            const _isUS = isUnitedStatesSelected(selectedCountry?.label || form1.country || "");
            const _secDigits = clampDigitsToFormat(_isUS, extractDigits(form1.securityId, _isUS));
            const _need = maxDigitsFor(_isUS);

            if (!_secDigits || _secDigits.length !== _need) {
                setFieldErrors((prev) => ({
                    ...(prev || {}),
                    securityId: `${_isUS ? "SSN" : "Security ID"} must be ${_need} digits`
                }));
                toast.error(`Please enter a valid ${_need}-digit ${_isUS ? "SSN" : "Security ID"} before saving Draft.`);
                try { securityRef?.current?.focus(); } catch { }
                return;
            }

            // Extra validation for non-US Security ID: MMDDXXXX where MM/DD are valid.
            if (!_isUS) {
                const mmddMsg = validateSecurityIdMmdd8(_secDigits);
                if (mmddMsg) {
                    setFieldErrors((prev) => ({
                        ...(prev || {}),
                        securityId: mmddMsg,
                        validateSecurity: ""
                    }));
                    toast.error("Please enter a valid Security ID (MMDDXXXX).", { toastId: "onb-securityid-mmdd" });
                    try { securityRef?.current?.focus(); } catch { }
                    return;
                }
            }
        }
        try {
            setLoading(true);

            const webphoneOverride = await validateWebphoneManagerBeforeApi();
            const payload = buildPayload("Draft", "Draft", webphoneOverride);
            const resp = await apiSaveDraft(payload);

            const respNpw = resp?.npw_id || resp?.npwId || resp?.data?.npw_id || resp?.data?.npwId;
            const respClient =
                resp?.client_request_id ||
                resp?.clientRequestId ||
                resp?.data?.client_request_id ||
                resp?.data?.clientRequestId;

            if (respNpw && dtvid) {
                // Persist the Draft NPW id per dtvid so /onboarding/new?dtvid=... always edits the same draft.
                setPersistedDraftNpwIdForDtvid(dtvid, String(respNpw));
            }

            if (isEditRoute) {
                if (respNpw) {
                    const v = String(respNpw);
                    setDraftNpwId(v);
                    sessionStorage.setItem("tb_draft_npw_id", v);
                }
                if (respClient) {
                    const v = String(respClient);
                    setDraftClientRequestId(v);
                    sessionStorage.setItem("tb_draft_client_request_id", v);
                }
            } else if (dtvid && respNpw) {
                // First time saving a Draft from /onboarding/new?dtvid=...: jump into edit route.
                // Show toast *before* navigation so the user can see the success message.
                const nextPath = `/onboarding/new/${encodeURIComponent(String(respNpw))}?dtvid=${encodeURIComponent(dtvid)}`;
                successThenNavigate("Draft Saved", nextPath, { replace: true });
            } else {
                // Default behavior: clear cached draft IDs so the next Draft/Submit creates a NEW record.
                setDraftNpwId(null);
                setDraftClientRequestId(null);
                try {
                    sessionStorage.removeItem("tb_draft_npw_id");
                    sessionStorage.removeItem("tb_draft_client_request_id");
                } catch { }
            }

            if (isEditRoute) {
                setEditData((prev) => ({
                    ...(prev || {}),
                    ...form1,
                    status: "Draft",
                    npw_id: respNpw ? String(respNpw) : prev?.npw_id || draftNpwId || null,
                    client_request_id: respClient
                        ? String(respClient)
                        : prev?.client_request_id || draftClientRequestId || null,
                }));
            } else {
                // In NEW mode, do not keep ids in editData (prevents updating the same record).
                setEditData(null);
            }

            // If we didn't navigate above, show the success toast here.
            if (!(dtvid && respNpw)) {
                toast.success(isEditRoute ? "Updated Successfully" : "Draft Saved");
            }
        } catch (e) {
            console.error(e);
            toast.error("Draft save failed");
        } finally {
            setLoading(false);
        }
    };

    const continueToReview = async () => {
        if (!ensureIdentityOrForceSSO()) return;
        const blocks = blockingLoadsForContinue(dataLoads, { timeoutMs: 20000 });
        if (blocks.length) {
            blocks.forEach((b) => {
                const toastOpts = { toastId: dataLoadToastId(b.status, b.field) };
                if (b.status === "error") toast.error(loadFailMessage(b.field), toastOpts);
                else if (b.status === "timeout") toast.warn(loadTimeoutMessage(b.field), toastOpts);
                else toast.info(loadOngoingMessage(b.field), toastOpts);
            });
            return;
        }
        if (!validateAll()) return;
        setStep(2);
    };

    const submitAll = async () => {
        if (!ensureIdentityOrForceSSO()) return;
        if (!validateAll()) return;
        if (requiresCpValidateBeforeSubmit && cpValidation.status !== "passed") {
            toast.error("Please click Validate and resolve all CP errors before submitting.");
            return;
        }

        try {
            setLoading(true);
            setSubmitting(true);

            const webphoneOverride = getValidatedWebphoneManagerForCurrentManager();
            if (!webphoneOverride) {
                toast.error("Please click Validate again so Webphone Manager ATTUID can be reused for submit.");
                setCpValidation({ status: "idle", message: "Please validate again" });
                return;
            }
            const payload = buildPayload("Submitted", "Submitted", webphoneOverride);
            await callNewOrEdit(payload);

            // Once submitted, this record is no longer a Draft; allow a fresh Draft next time.
            if (dtvid) clearPersistedDraftNpwIdForDtvid(dtvid);

            const msg = hasExistingNpw() ? "Updated Successfully" : "Submitted Successfully";

            // Always clear any cached draft IDs once final submit succeeds.
            setDraftNpwId(null);
            setDraftClientRequestId(null);
            try {
                sessionStorage.removeItem("tb_draft_npw_id");
                sessionStorage.removeItem("tb_draft_client_request_id");
            } catch { }
            successThenDashboard(msg);
            // Navigation handled by successThenDashboard.
            return;
        } catch (e) {
            console.error(e);
            const messages = getCpErrorMessages(e, { max: 12 });
            if (messages.length) {
                messages.forEach((msg) => toast.error(msg));
            } else {
                const backendMsg =
                    e?.body?.error?.message ||
                    e?.body?.message ||
                    e?.data?.error?.message ||
                    e?.data?.message ||
                    null;

                const msg =
                    backendMsg ||
                    (typeof e?.message === "string" && e.message.trim() ? e.message : null) ||
                    (e?.status ? `Submit failed (HTTP ${e.status})` : "Submit failed");

                toast.error(msg);
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const validateBeforeSubmit = async () => {
        if (!requiresCpValidateBeforeSubmit) {
            setCpValidation({ status: "passed", message: "Validation not required" });
            toast.success("Validation not required for this onboarding flow.");
            return;
        }
        if (!ensureIdentityOrForceSSO()) return;
        if (!validateAll()) {
            toast.error("Please fix validation errors before validating.");
            return;
        }
        const validateNpwIdRaw = draftNpwId || effectiveNpwId || editData?.npw_id || editData?.npwId || "";
        const validateNpwId = /^\d+$/.test(String(validateNpwIdRaw).trim()) ? String(validateNpwIdRaw).trim() : "";
        const pick = (...vals) => {
            for (const val of vals) {
                const s = String(val ?? "").trim();
                if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return s;
            }
            return "";
        };
        const attRoleKeyForValidate = pick(form1?.attRoleKey, form1?.att_role_key, editData?.attRoleKey, editData?.att_role_key);
        const profileFromAttRoleKey = (() => {
            const idx = String(attRoleKeyForValidate || "").indexOf("||");
            return idx >= 0 ? String(attRoleKeyForValidate).slice(idx + 2).trim() : "";
        })();
        const roleFromAttRoleKey = (() => {
            const idx = String(attRoleKeyForValidate || "").indexOf("||");
            return idx >= 0 ? String(attRoleKeyForValidate).slice(0, idx).trim() : "";
        })();
        const derivedProfileFromCatalog = (() => {
            const cpRoleId = pick(form1?.attRoleCpRoleId, form1?.att_role_cp_role_id, roleFromAttRoleKey, form1?.attRoleKey);
            if (!cpRoleId) return "";
            for (const role of hingeRoleCatalog || []) {
                for (const cp of role?.cinglepointRoles || []) {
                    if (String(cp?.cpRoleId || "").trim() !== cpRoleId) continue;
                    const profile = (cp?.cpProfiles || [])[0];
                    const cpProfileId = String(profile?.cpProfileId || "").trim();
                    if (cpProfileId) return cpProfileId;
                }
            }
            return "";
        })();
        const cpProfileId = pick(
            form1?.attRoleCpProfileId,
            form1?.att_role_cp_profile_id,
            form1?.cinglepointProfileId,
            form1?.singlepointProfileId,
            editData?.attRoleCpProfileId,
            editData?.att_role_cp_profile_id,
            editData?.cinglepointProfileId,
            editData?.singlepointProfileId,
            profileFromAttRoleKey,
            derivedProfileFromCatalog
        );
        if (!cpProfileId) {
            const msg = "CP Profile ID is required for validation.";
            setCpValidation({ status: "failed", message: msg });
            toast.error(msg);
            return;
        }
        const ssnSecId = extractDigits(
            pick(
                form1?.securityId,
                form1?.validateSecurity,
                form1?.confirmSecurityId,
                form1?.security_id_plaintext,
                form1?.securityIdPlaintext
            ),
            isUS
        );

        const webphoneOverride = await validateWebphoneManagerBeforeApi();
        const validatedWebphoneAttuid = webphoneOverride?.webphoneManagerAttuid || webphoneOverride?.webphone_manager_attuid || null;

        const requestDetail = {
            cp_approver_attuid: pick(form1?.sponsor, validatedWebphoneAttuid, sessionStorage.getItem("tb_attuid")),
            first_name: pick(form1?.firstName, editData?.first_name),
            last_name: pick(form1?.lastName, editData?.last_name),
            email: pick(form1?.email, editData?.email),
            phone: pick(form1?.phone, editData?.phone, editData?.phone_number),
            phone_number: pick(form1?.phone, editData?.phone, editData?.phone_number),
            country: pick(form1?.citizenship, editData?.citizenship, form1?.country, editData?.country),
            countryOfWorking: pick(form1?.country, editData?.country),
            attuid: pick(form1?.attuid, editData?.attuid),
            dtvid: pick(form1?.dtvId, dtvid, editData?.dtv_id, editData?.dtvId),
            worker_type_tax: pick(workerTypeTax, form1?.workerTypeTax, editData?.worker_type_tax, editData?.w2_1099),
            w2_1099: pick(workerTypeTax, form1?.workerTypeTax, editData?.worker_type_tax, editData?.w2_1099),
            webphone_manager_attuid: validatedWebphoneAttuid,
            cp_role_id: pick(form1?.attRoleCpRoleId, form1?.att_role_cp_role_id, editData?.att_role_cp_role_id),
            role: pick(form1?.role, editData?.role),
            cp_profile_id: cpProfileId,
            sow: cpProfileId,
            security_id_hash: pick(form1?.security_id_hash, form1?.securityIdHash) || null,
            security_id_plaintext: null,
            securityId: null,
            security_id: null,
            ssn: null,
            ssn_plain: null,
            secid: null,
            sec_id: null,
            start_date: serializeDate(form1?.startDate) || pick(editData?.start_date, new Date().toISOString().slice(0, 10)),
            end_date: serializeDate(form1?.endDate) || pick(editData?.end_date) || null,
            comments: "Validation from UI no-npw flow",
            fte_nominated: true,
            offboard_by_attuid: false,
            ...(ssnSecId ? { ssnSecId } : {}),
        };
        try {
            setLoading(true);
            setCpValidation({ status: "checking", message: "Validating with CP API..." });

            const validateStatusKey = String(editData?.personStatus ?? editData?.person_status ?? editData?.status ?? "")
                .trim()
                .toLowerCase()
                .replace(/[\s_-]/g, "");
            const shouldSendFullValidatePayload = !validateNpwId || validateStatusKey === "draft";

            const payload = {
                req_type: "action",
                action: "VALIDATE",
                client_request_id: `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                request_detail: requestDetail,
                ...(!shouldSendFullValidatePayload && validateNpwId ? { npw_id: Number(validateNpwId) } : {}),
            };
            const resp = await apiValidateCp(payload);

            const failed = hasApiValidationFailure(resp);
            const messages = getCpErrorMessages(resp, { max: 12 });

            if (failed) {
                if (messages.length) {
                    messages.forEach((msg) => toast.error(msg));
                } else {
                    toast.error("CP validation failed. Please review and correct the data.");
                }
                setCpValidation({ status: "failed", message: messages[0] || "Validation failed" });
                return;
            }

            setCpValidation({ status: "passed", message: "Validation successful" });
            toast.success("Validation successful. You can submit now.");
        } catch (e) {
            const messages = getCpErrorMessages(e, { max: 12 });
            const backendMsg =
                e?.body?.error?.message ||
                e?.body?.message ||
                e?.data?.error?.message ||
                e?.data?.message ||
                null;
            const msg =
                messages[0] ||
                backendMsg ||
                (typeof e?.message === "string" && e.message.trim() ? e.message : null) ||
                "Validation failed";
            setCpValidation({ status: "failed", message: String(msg) });
            if (messages.length) {
                messages.forEach((item) => toast.error(item));
            } else {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppShell>
            <ToastContainer />
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                        Loading...
                    </div>
                </div>
            )}
            {/* {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                        Loading...
                    </div>
                </div>
            )} */}

            <div className="ap-page-wrap page-scroll-scope">
                <div className="onb-wrap compact">
                    <style>{`
            .fte-stepper{display:flex;align-items:flex-start;justify-content:center;margin:0 auto 24px;gap:0;}
            .fte-step{display:flex;flex-direction:column;align-items:center;min-width:120px;}
            .fte-circle{width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;
              font-size:14px;font-weight:700;box-sizing:border-box;line-height:1;}
            .fte-circle.done{background:#2f5fd0;color:#fff;border:2px solid #2f5fd0;}
            .fte-circle.active{background:#2f5fd0;color:#fff;border:2px solid #2f5fd0;}
            .fte-circle.inactive{background:#fff;color:#5f6b7a;border:2px solid #9aa4b2;}
            .fte-label{margin-top:6px;font-size:13px;color:#5f6b7a;white-space:nowrap;text-align:center;}
            .fte-line{height:2px;width:360px;background:#2f5fd0;margin-top:12px;margin-left:-48px;margin-right:-48px;}

            /* NEW: input-with-eye INSIDE the input */
            .sec-wrap{display:flex;flex-direction:column;gap:4px;min-width:240px;}
            .sec-input-shell{
              position:relative;
              display:inline-block;
              width:100%;
              max-width:360px;
            }
            .sec-input-shell input{
              width:100%;
              padding-right:42px; /* space for eye */
              box-sizing:border-box;
            }
            .sec-eye-inside{
              position:absolute;
              right:8px;
              top:50%;
              transform:translateY(-50%);
              height:26px;
              width:30px;
              border:0;
              background:transparent;
              cursor:pointer;
              display:flex;
              align-items:center;
              justify-content:center;
              color:#334155;
              padding:0;
            }
            .sec-eye-inside:hover{opacity:0.8;}
            .sec-hint{font-size:12px;color:#64748b;}
          `}</style>

                    <h2 className="onb-title">
                        {isReactiveAttuidFlow
                            ? "Reactivate ATTUID"
                            : (dtvPrefillHeadingOverride || editHeadingOverride || (isEditRoute ? "Edit Onboarding Request (FTE)" : "New Onboarding Request (FTE)"))}
                    </h2>

                    <div style={{ display: "flex", justifyContent: "flex-start", marginTop: 10, marginBottom: 14 }}>
                        <div
                            style={{
                                background: "#ffffff",
                                color: "#222",
                                padding: "6px 12px",
                                borderRadius: 14,
                                fontWeight: 500,
                                fontSize: 12,
                                border: "1px solid #d0d7de",
                            }}
                        >
                            Worker Type: <span style={{ fontWeight: 700 }}>{workerTypeLabel}</span>
                        </div>
                    </div>

                    <div className="fte-stepper">
                        <div className="fte-step">
                            <div className={`fte-circle ${step > 1 ? "done" : step === 1 ? "active" : "inactive"}`}>1</div>
                            <div className="fte-label">Enter Details</div>
                        </div>

                        <div className="fte-line" />

                        <div className="fte-step">
                            <div className={`fte-circle ${step >= 2 ? "done" : step === 2 ? "active" : "inactive"}`}>2</div>
                            <div className="fte-label">Review &amp; Submit</div>
                        </div>
                    </div>

                    {step === 1 && (
                        <div
                            className="grid-3"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3,1fr)",
                                gap: 35,
                                padding: "24px 40px 40px",
                            }}
                        >
                            <div>
                                <Section title="Personal Details">
                                    <Row label="First Name">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <input
                                                value={form1.firstName}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setForm1({ ...form1, firstName: v });
                                                    runFieldValidation("firstName", v);
                                                }}
                                            />
                                            {fieldErrors.firstName && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.firstName}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="Last Name">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <input
                                                value={form1.lastName}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setForm1({ ...form1, lastName: v });
                                                    runFieldValidation("lastName", v);
                                                }}
                                            />
                                            {fieldErrors.lastName && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.lastName}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="Email">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <input
                                                value={form1.email}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setForm1({ ...form1, email: v });
                                                    runFieldValidation("email", v);
                                                }}
                                            />
                                            {fieldErrors.email && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.email}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="Phone" NonMandatory={true}>
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <input
                                                placeholder={isUnitedStatesSelected(form1.country) ? "1st & 4th digits between 2-9" : ""}
                                                value={form1.phone}
                                                onChange={(e) => {
                                                    const maxPhoneLen = isUnitedStatesSelected(form1.country) ? 10 : 15;
                                                    const digitsOnly = String(e.target.value || "")
                                                        .replace(/\D/g, "")
                                                        .slice(0, maxPhoneLen);
                                                    setForm1({ ...form1, phone: digitsOnly });
                                                    runFieldValidation("phone", digitsOnly);
                                                }}
                                            />
                                            {fieldErrors.phone && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.phone}</span>
                                            )}
                                        </div>
                                    </Row>

                                    {showDtvidAttuidPrefill && (
                                        <>
                                            {Boolean(String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || effectiveDtvid || "").trim()) && (
                                                <Row label="DTVID" NonMandatory rdOnly inline>
                                                    <b style={{ marginLeft: "-100px", wordBreak: "break-word" }}>
                                                        {String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || effectiveDtvid || "").trim()}
                                                    </b>
                                                </Row>
                                            )}
                                            {Boolean(String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim()) && (
                                                <Row label="ATTUID" NonMandatory rdOnly inline>
                                                    <b style={{ marginLeft: "-100px", wordBreak: "break-word" }}>
                                                        {String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim()}
                                                    </b>
                                                </Row>
                                            )}
                                        </>
                                    )}
                                    <Row label="Country of Citizenship">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <Select
                                                options={(citizenshipOptions && citizenshipOptions.length) ? citizenshipOptions : countryOptions}
                                                value={ctzn}
                                                onChange={(v) => {
                                                    setCtz(v);
                                                    const label = v?.label || "";
                                                    setForm1({ ...form1, citizenship: label });
                                                    runFieldValidation("citizenship", label);
                                                }}
                                                isSearchable
                                                styles={wideSelectStyles}
                                            />
                                            {fieldErrors.citizenship && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.citizenship}</span>
                                            )}
                                        </div>
                                    </Row>
                                </Section>
                            </div>

                            <div>
                                <Section title="Sponsorship & Role">
                                    <Row label="Country">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <Select
                                                options={countryOptions}
                                                value={selectedCountry}
                                                onChange={(v) => {
                                                    setSelectedCountry(v);
                                                    setSelectedState(null);
                                                    setSelectedCity(null);
                                                    const label = v?.label || "";
                                                    setForm1({ ...form1, country: label });
                                                    runFieldValidation("country", label);
                                                }}
                                                isSearchable
                                                styles={wideSelectStyles}
                                            />
                                            {fieldErrors.country && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.country}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="State" NonMandatory={!isStateRequired}>
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <Select
                                                options={stateOptions}
                                                value={selectedState}
                                                onChange={(v) => {
                                                    setSelectedState(v);
                                                    setSelectedCity(null);
                                                    const label = v?.label || "";
                                                    setForm1({ ...form1, state: label });
                                                    runFieldValidation("state", label);
                                                }}
                                                isDisabled={!selectedCountry}
                                                styles={wideSelectStyles}
                                            />
                                            {fieldErrors.state && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.state}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="City" NonMandatory={!isCityRequired}>
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <Select
                                                options={cityOptions}
                                                value={selectedCity}
                                                onChange={handleCity}
                                                isDisabled={!selectedState}
                                                styles={wideSelectStyles}
                                            />
                                            {fieldErrors.city && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.city}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="Time Zone" NonMandatory={true}>
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <Select
                                                options={timeZoneOptions}
                                                value={selectedTz}
                                                onChange={(e) => {
                                                    const label = e?.label || "";
                                                    setForm1({ ...form1, timeZone: label });
                                                    setSelectedTz(e);
                                                    runFieldValidation("timeZone", label);
                                                }}
                                                isSearchable
                                                styles={wideSelectStyles}
                                            />
                                            {fieldErrors.timeZone && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.timeZone}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="Occupancy">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <select
                                                value={form1.occupancyCode}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setForm1({ ...form1, occupancyCode: v });
                                                    runFieldValidation("occupancyCode", v);
                                                }}
                                                style={{ minWidth: 200 }}
                                            >
                                                <option value="">Select</option>
                                                {STATIC_DD.occupancyCode.map((x) => (
                                                    <option key={x}>{x}</option>
                                                ))}
                                            </select>
                                            {fieldErrors.occupancyCode && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.occupancyCode}</span>
                                            )}
                                        </div>
                                    </Row>
                                    <Row label="Worker Type (W2 / 1099 / International)">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <select
                                                value={workerTypeTax}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setWorkerTypeTax(v);
                                                    runFieldValidation("workerTypeTax", v);
                                                }}
                                                style={{ minWidth: 200 }}
                                            >
                                                <option value="">Select</option>
                                                {STATIC_DD.workerTypeTax.map((x) => (
                                                    <option key={x}>{x}</option>
                                                ))}
                                            </select>
                                            {fieldErrors.workerTypeTax && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.workerTypeTax}</span>
                                            )}
                                        </div>
                                    </Row>
                                </Section>
                            </div>

                            <div>
                                <Section title="Contract & Assignment Details">
                                    <Row label="Start Date">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <DatePicker
                                                selected={form1.startDate}
                                                onChange={(d) => {
                                                    setForm1({
                                                        ...form1,
                                                        startDate: d,
                                                        endDate: form1.endDate && d && form1.endDate < d ? null : form1.endDate,
                                                    });
                                                    runFieldValidation("startDate", d);
                                                }}
                                                dateFormat="MM/dd/yyyy"
                                                minDate={minStart}
                                                maxDate={maxStart}
                                            />
                                            {fieldErrors.startDate && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.startDate}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="End Date">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <DatePicker
                                                selected={form1.endDate}
                                                onChange={(d) => {
                                                    setForm1({ ...form1, endDate: d });
                                                    runFieldValidation("endDate", d);
                                                }}
                                                dateFormat="MM/dd/yyyy"
                                                disabled={!form1.startDate}
                                                minDate={minEnd}
                                            />
                                            {fieldErrors.endDate && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.endDate}</span>
                                            )}
                                        </div>
                                    </Row>

                                    {/* SECURITY ID (eye INSIDE input) */}
                                    <Row
                                        NonMandatory={isSecurityLocked}
                                        label={
                                            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "relative", zIndex: 3, pointerEvents: "auto" }}>
                                                <span>{securityLabel}</span>
                                                {showSecurityEditButton && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleEnableSecurityEdit();
                                                        }}
                                                        style={{
                                                            border: "1px solid #cbd5e1",
                                                            background: "#fff",
                                                            color: "#0f172a",
                                                            padding: "2px 10px",
                                                            borderRadius: 6,
                                                            cursor: "pointer",
                                                            fontSize: 12,
                                                            lineHeight: "20px"
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {showSecurityDisableEditButton && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleDisableSecurityEdit();
                                                        }}
                                                        style={{
                                                            border: "1px solid #cbd5e1",
                                                            background: "#fff",
                                                            color: "#0f172a",
                                                            padding: "2px 10px",
                                                            borderRadius: 6,
                                                            cursor: "pointer",
                                                            fontSize: 12,
                                                            lineHeight: "20px"
                                                        }}
                                                    >
                                                        Disable
                                                    </button>
                                                )}
                                            </span>
                                        }
                                    >
                                        <div className="sec-wrap" style={{ position: "relative", zIndex: 1 }}>
                                            <div className="sec-input-shell">
                                                <input
                                                    ref={securityRef}
                                                    value={securityDisplayValue}
                                                    placeholder={securityPlaceholder}
                                                    inputMode="numeric"
                                                    autoComplete="off"
                                                    disabled={isSecurityLocked}
                                                    readOnly={isSecurityLocked}
                                                    title={isSecurityLocked ? `Click Edit to update ${securityLabel}` : ""}
                                                    style={{ backgroundColor: isSecurityLocked ? "#e5e7eb" : undefined, cursor: isSecurityLocked ? "not-allowed" : undefined, opacity: isSecurityLocked ? 0.9 : 1 }}
                                                    onChange={handleSecChange("securityId")}
                                                    onKeyDown={handleSecKeyDown("securityId")}
                                                    onPaste={handleSecPaste("securityId")}
                                                    onFocus={handleSecFocus("securityId")}
                                                    onBlur={() => { if (!isSecurityLocked) runFieldValidation("securityId", form1.securityId); }}
                                                />
                                                <button
                                                    type="button"
                                                    className="sec-eye-inside"
                                                    aria-label={showSecurityId ? `Hide ${securityLabel}` : `Show ${securityLabel}`}
                                                    title={showSecurityId ? `Hide ${securityLabel}` : `Show ${securityLabel}`}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => {
                                                        setShowSecurityId((p) => !p);
                                                        if (!isSecurityLocked) queueMicrotask(() => securityRef.current?.focus());
                                                    }}
                                                >
                                                    {/* {showSecurityId ? "👁" : "👁"} */}
                                                    {showSecurityId ? <EyeIcon /> : <EyeIcon />}
                                                </button>
                                            </div>

                                            <div className="sec-hint">{securityHint}</div>

                                            {ssnAssociation?.status === "checking" && (
                                                <div style={{ fontSize: 12, color: "#64748b" }}>
                                                    Checking {securityLabel}…
                                                </div>
                                            )}
                                            {ssnAssociation?.status === "found" && ssnAssociation?.message && (
                                                <div style={{ fontSize: 12, color: "#b45309", marginTop: 2 }}>
                                                    {ssnAssociation.message}
                                                </div>
                                            )}
                                            {ssnAssociation?.status === "none" && ssnAssociation?.message && (
                                                <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>
                                                    {ssnAssociation.message}
                                                </div>
                                            )}

                                            {fieldErrors.securityId && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.securityId}</span>
                                            )}
                                        </div>
                                    </Row>

                                    {/* CONFIRM SECURITY ID (eye INSIDE input) */}
                                    <Row NonMandatory={isSecurityLocked} label={confirmSecurityLabel}>
                                        <div className="sec-wrap">
                                            <div className="sec-input-shell">
                                                <input
                                                    ref={confirmSecurityRef}
                                                    value={confirmDisplayValue}
                                                    placeholder={securityPlaceholder}
                                                    inputMode="numeric"
                                                    autoComplete="off"
                                                    disabled={isSecurityLocked}
                                                    readOnly={isSecurityLocked}
                                                    title={isSecurityLocked ? `Click Edit to update ${securityLabel}` : ""}
                                                    style={{ backgroundColor: isSecurityLocked ? "#e5e7eb" : undefined, cursor: isSecurityLocked ? "not-allowed" : undefined, opacity: isSecurityLocked ? 0.9 : 1 }}
                                                    onChange={handleSecChange("validateSecurity")}
                                                    onKeyDown={handleSecKeyDown("validateSecurity")}
                                                    onPaste={handleSecPaste("validateSecurity")}
                                                    onFocus={handleSecFocus("validateSecurity")}
                                                    onBlur={() => { if (!isSecurityLocked) runFieldValidation("validateSecurity", form1.validateSecurity); }}
                                                />
                                                <button
                                                    type="button"
                                                    className="sec-eye-inside"
                                                    aria-label={showConfirmSecurityId ? `Hide ${confirmSecurityLabel}` : `Show ${confirmSecurityLabel}`}
                                                    title={showConfirmSecurityId ? `Hide ${confirmSecurityLabel}` : `Show ${confirmSecurityLabel}`}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => {
                                                        setShowConfirmSecurityId((p) => !p);
                                                        if (!isSecurityLocked) queueMicrotask(() => confirmSecurityRef.current?.focus());
                                                    }}
                                                >
                                                    {showConfirmSecurityId ? <EyeIcon /> : <EyeIcon />}
                                                    {/* {showConfirmSecurityId ? "👁" : "👁"} */}
                                                </button>
                                            </div>

                                            <div className="sec-hint">{securityHint}</div>

                                            {fieldErrors.validateSecurity && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.validateSecurity}</span>
                                            )}
                                        </div>
                                    </Row>



                                    <Row label="ATT Role">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <Select
                                                options={attRoleSelectOptions}
                                                value={selectedAttRoleOption}
                                                onChange={(o) => {
                                                    const m = o?.meta;
                                                    const k = o?.value || "";
                                                    setForm1({
                                                        ...form1,
                                                        attRoleKey: k,
                                                        attRoleDtvRoleId: m?.dtvRoleId || "",
                                                        attRoleDtvRoleName: m?.dtvRoleName || "",
                                                        attRoleCpRoleId: m?.cpRoleId || "",
                                                        attRoleCpRoleDesc: m?.cpRoleDesc || "",
                                                        attRoleCpProfileId: "",
                                                        attRoleCpProfileDesc: "",
                                                    });
                                                    runFieldValidation("attRoleKey", k);
                                                }}
                                                styles={wideSelectStyles}
                                            />
                                            {fieldErrors.attRoleKey && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.attRoleKey}</span>
                                            )}
                                        </div>
                                    </Row>

                                    <Row label="Billable (Yes / No)">
                                        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 240px) 1fr", alignItems: "center", columnGap: 8 }}>
                                            <select
                                                value={nonBillable}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setNonBillable(v);
                                                    runFieldValidation("nonBillable", v);
                                                }}
                                                style={{ minWidth: 200 }}
                                            >
                                                <option value="">Select</option>
                                                {STATIC_DD.nonBillable.map((x) => (
                                                    <option key={x}>{x}</option>
                                                ))}
                                            </select>
                                            {fieldErrors.nonBillable && (
                                                <span style={{ color: "red", fontSize: 12 }}>{fieldErrors.nonBillable}</span>
                                            )}
                                        </div>
                                    </Row>
                                </Section>
                            </div>
                        </div>
                    )}

                    {/* REVIEW */}
                    {step === 2 && (
                        <div
                            className="grid-3"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3,1fr)",
                                gap: 35,
                                padding: "24px 40px 40px",
                            }}
                        >
                            <Overview
                                title="Personal Details"
                                setStep={setStep}
                                form={1}
                                items={{
                                    "First Name": form1.firstName,
                                    "Last Name": form1.lastName,
                                    Email: form1.email,
                                    Phone: form1.phone,
                                    ...(showDtvidAttuidPrefill
                                        ? {
                                            ...(String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || effectiveDtvid || "").trim()
                                                ? { DTVID: String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || effectiveDtvid || "").trim() }
                                                : {}),
                                            ...(String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim()
                                                ? { ATTUID: String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim() }
                                                : {})
                                        }
                                        : {}),
                                    "Country of Citizenship": form1.citizenship,
                                }}
                            />

                            <Overview
                                title="Sponsorship & Role"
                                setStep={setStep}
                                form={1}
                                items={{
                                    Country: form1.country,
                                    State: form1.state,
                                    City: form1.city,
                                    "Time Zone": form1.timeZone,
                                    Occupancy: form1.occupancyCode,
                                    "Worker Type (W2 / 1099 / International)": workerTypeTax,
                                }}
                            />

                            <Overview
                                title="Contract & Assignment Details"
                                setStep={setStep}
                                form={1}
                                items={{
                                    "Start Date": form1.startDate && fmtDate(form1.startDate),
                                    "End Date": form1.endDate && fmtDate(form1.endDate),
                                    [securityLabel]: form1.securityId
                                        ? buildMaskedLast4(isUS, clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS)))
                                        : "-",
                                    // [confirmSecurityLabel]: form1.validateSecurity
                                    //     ? buildMaskedLast4(isUS, clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS)))
                                    //     : "-",
                                    "Worker Type": workerTypeLabel,

                                    "ATT Role":
                                        form1.attRoleKey &&
                                        `${form1.attRoleCpRoleId}${form1.attRoleCpRoleDesc ? " - " + form1.attRoleCpRoleDesc : ""}`,
                                    "Billable": nonBillable,
                                }}
                            />
                        </div>
                    )}

                    {/* FOOTER */}
                    <div className="onb-actions" style={{ paddingRight: 100 }}>
                        <div className="right" style={{ display: "flex", gap: 12 }}>
                            {step > 1 ? (
                                <button
                                    className="button back"
                                    onClick={() => {
                                        if (isEditRoute && hasExternalCallerTarget) {
                                            handleBackToCaller();
                                            return;
                                        }
                                        setStep(step - 1);
                                    }}
                                >
                                    « Back
                                </button>
                            ) : (
                                editData && (
                                    <button className="button back" onClick={handleBackToCaller}>
                                        « Back
                                    </button>
                                )
                            )}

                            {(editData === null || editData?.status === "Draft") && (
                                <button className="button back" onClick={saveDraft} disabled={loading}>
                                    Save Draft
                                </button>
                            )}

                            {step < 2 ? (
                                <button className="btn primary" onClick={continueToReview}>
                                    Continue »
                                </button>
                            ) : (
                                <>
                                    {requiresCpValidateBeforeSubmit && (
                                        <button
                                            className="button back"
                                            onClick={validateBeforeSubmit}
                                            disabled={loading || submitting || cpValidation.status === "checking"}
                                        >
                                            {cpValidation.status === "checking" ? "Validating..." : "Validate"}
                                        </button>
                                    )}
                                    <button
                                        className="btn primary"
                                        onClick={submitAll}
                                        disabled={submitting || loading || (requiresCpValidateBeforeSubmit && cpValidation.status !== "passed")}
                                        title={requiresCpValidateBeforeSubmit && cpValidation.status !== "passed" ? "Run Validate first" : undefined}
                                    >
                                        {submitting ? "Submitting..." : "Submit"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}

/* COMPONENTS */
function Section({ title, children }) {
    return (
        <div className="onb-section">
            <div className="onb-section-title">
                {title}
                <hr style={{ border: "none", borderTop: "1px solid #ddd" }} />
            </div>
            <div className="onb-section-body">{children}</div>
        </div>
    );
}

function Row({ label, children, rdOnly, NonMandatory = false, inline = false }) {
    return (
        <div className={`row ${inline ? "row-inline" : ""}`}>
            <label>
                <span style={{ display: "inline-flex", alignItems: "center", width: "100%" }}>
                    {!rdOnly && !NonMandatory && (
                        <span style={{ marginRight: 4 }}>*</span>
                    )}
                    <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
                </span>
            </label>
            {!rdOnly && !inline && <br />}
            {children}
        </div>
    );
}

function Overview({ title, items, setStep, form }) {
    const getItemValue = (obj, keyRegex) => {
        if (!obj || typeof obj !== "object") return undefined;
        for (const [k, v] of Object.entries(obj)) {
            if (keyRegex.test(String(k || ""))) return v;
        }
        return undefined;
    };
    const isTruthyYes = (v) => {
        if (v === true) return true;
        const s = String(v ?? "").trim().toLowerCase();
        return s === "yes" || s === "true" || s === "1" || s === "y";
    };
    const isUnitedStatesish = (v) => {
        const s0 = String(v ?? "").trim().toLowerCase();
        if (!s0) return false;
        const s = s0.replace(/[.()]/g, "").replace(/\s+/g, " ").trim();
        if (s === "us" || s === "u s" || s === "usa") return true;
        return s.includes("united states");
    };

    if (!window.__npwOverviewCtx) window.__npwOverviewCtx = { attuidOn: false, isUS: false };
    try {
        const attuidVal = getItemValue(items, /^\s*attuid\s*required\s*\??\s*$/i);
        const countryVal = getItemValue(items, /^\s*country\s*$/i);
        if (attuidVal !== undefined) window.__npwOverviewCtx.attuidOn = isTruthyYes(attuidVal);
        if (countryVal !== undefined) window.__npwOverviewCtx.isUS = isUnitedStatesish(countryVal);
    } catch { /* ignore */ }

    const baseSecurityLabel = window.__npwOverviewCtx?.isUS ? "SSN #" : "Security ID #";
    const normalizeOverviewLabel = (raw) => {
        const s = String(raw || "").trim();
        if (!s) return s;
        if (/^confirm\s+(?:security\s*id|ssn)\b/i.test(s)) return `Confirm ${baseSecurityLabel}`;
        if (/^(?:security\s*id|ssn)\b/i.test(s)) return baseSecurityLabel;
        return s;
    };

    const normalizeOverviewValue = (rawKey, rawValue) => {
        const raw = String(rawKey || "");
        const normalizedKey = normalizeOverviewLabel(raw);
        const isSecurityKey =
            /(?:^|\b)(?:confirm\s+)?(?:security\s*id|ssn)\b/i.test(raw) ||
            /(?:^|\b)(?:confirm\s+)?(?:security\s*id|ssn)\b/i.test(normalizedKey);

        if (!isSecurityKey) {
            if (rawValue == null) return null;
            const s = String(rawValue).trim();
            if (!s || s === "-") return null;
            return s;
        }

        const s = rawValue == null ? "" : String(rawValue).trim();
        if (!s || s === "-" || /^[*]+$/.test(s)) return "********";
        return s;
    };

    return (
        <div className="onb-section">
            <div
                className="onb-section-title"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
                <span>{title}</span>
                <span className="edit-pointer" onClick={() => setStep(form)} title="Edit">
                    ✎
                </span>
            </div>
            <div className="onb-section-body">
                {Object.entries(items).map(([k, v]) => {
                    const displayValue = normalizeOverviewValue(k, v);
                    if (displayValue === null) return null;

                    return (
                        <div className="row" key={k}>
                            <label>{normalizeOverviewLabel(k)}</label>
                            <br />
                            <b>{String(displayValue)}</b>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function StepDot({ n, label, active, done }) {
    return (
        <div className="step-dot">
            <div className={`dot ${done ? "done" : ""} ${active ? "active" : ""}`}>{n}</div>
            <div className="dot-label">{label}</div>
        </div>
    );
}

function StepBar({ done, style }) {
    return <div className={`step-bar ${done ? "done" : ""}`} style={style} />;
}
